import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { default as ReactMarkdown } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { useDispatch, useSelector } from "react-redux";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { Image, Popup, Modal, Header, Grid, Form, Radio, Input, Accordion, Card } from "semantic-ui-react";
import { useLocation, useNavigate } from "react-router-dom";
import StarRating from "../../components/StarRating";
import CustomToaster from "../../components/CustomToaster";
import "../../styles/ChatBot.css";
import "../../styles/External-user.css";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import { formatMessageTimestamp, formatMessageTimestampFull } from "../../utils/formatMessageTimestamp";

// Import organization details action for session tracking
import { fetchOrganizationDetails } from "../../actions/adminConsoleAction";

// Reuse existing APIs/actions
import { getQnaChatbotData, addEngagementAPI, updateHumanInLoopRefs, getImageBlob, getFileContentBlob } from "../../apis";
import AuthenticatedImage, { BLANK_IMAGE_PLACEHOLDER } from "../../components/AuthenticatedImage";
import {
  createChatHistoryData,
  SaveHistoryConversation,
  feedbackServiceRequest,
  getDefaultFeedbackSuggestion,
  getChatHistoryConversation,
  // Agent-Help actions
  getAgentEmail,
  humanAgentRegister,
  humanAgentQna,
  humanAgentGetStatus,
  humanAgentUpdateStatus,
  getHumanAgentQnaChat,
  humanAgentValidate,
  resetUserQuestion,
  resetAgentResponse,
  resetAgentStatus,
  resetQuestion,
  resetAnswer,
  // Fallback: reuse email support retrieval to infer email when agent email API returns empty
  getEmailSupport,
  // ✅ Added this
  fetchDataSetParameters,
  // ✅ Fetch doc settings (serviceAgentEnabled comes from here)
  fetchDocSettings,
  // ✅ Token usage tracking for EXT users
  saveTokenUsageAction,
} from "../../actions/dataConsoleAction";
import PDFViewer from "../Dataconsole/PDFViewer";
import { notifyAgentEvent, clearAgentNotifications } from "../../utils/agentNotify";
import { injectAgentHelpLink, buildAgentHelpAnchorRenderer } from "../../utils/liveAgentHelp";
import "react-image-lightbox/style.css";
import Lightbox from "react-image-lightbox";
import AgentRequestStatus from "../../components/AgentRequestStatus";

// Dataset and chat identifiers are provided via redirect URL query params

// Polite waiting messages shown when bot takes longer than 15 seconds
const delayedWaitingMessages = [
  "Still working on your request — thank you for your patience!",
  "Hang tight! Our bot is carefully crafting the best response for you.",
  "Almost there — great answers take a little extra time!",
  "Our bot is doing some heavy thinking — please bear with us!",
  "Thank you for waiting — we're making sure you get the most helpful answer.",
  "Good things come to those who wait — your response is on its way!",
  "We appreciate your patience — the bot is working hard behind the scenes!",
  "Just a moment longer — we want to make sure we get this right for you.",
  "Your question deserves a thoughtful answer — we'll be right with you!",
  "The bot is digging deep for the best response — thanks for hanging in there!",
];

// Lightweight loader dots used in normal flow
const LoadingDots = () => {
  const [waitingMessage, setWaitingMessage] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      const randomIndex = Math.floor(Math.random() * delayedWaitingMessages.length);
      setWaitingMessage(delayedWaitingMessages[randomIndex]);
    }, 12000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="loading-dots-container">
      <span className="loading-dots">
        <span>•</span>
        <span>•</span>
        <span>•</span>
      </span>
      {waitingMessage && (
        <div className="delayed-waiting-message">{waitingMessage}</div>
      )}
    </div>
  );
};

// Typewriter effect for bot responses (markdown-aware)
const mdComponents = {
  table: ({ children, ...props }) => (
    <div className="table-scroll-wrapper">
      <table {...props}>{children}</table>
    </div>
  ),
};

const TypewriterMarkdown = ({ text, speed = 10, onCharRender, onComplete, components }) => {
  const [typedText, setTypedText] = useState("");
  useEffect(() => {
    setTypedText("");
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setTypedText(text.slice(0, i));
      if (onCharRender) onCharRender();
      if (i >= text.length) {
        clearInterval(interval);
        if (onComplete) onComplete();
      }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={components || mdComponents}>
      {typedText}
    </ReactMarkdown>
  );
};

function ExternalUser() {
  const dispatch = useDispatch();
  const userTokenFromRedux = useSelector((state) => state.auth.token);
  const userRole = userTokenFromRedux ? userTokenFromRedux.extension_Role : "";
  let trigger = useNavigate();

  let userToken = userTokenFromRedux;

  // ✅ Add fallback for early load cases (before Redux updates)
  if (!userToken) {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      try {
        const decoded = jwtDecode(storedToken);
        userToken = decoded;
        console.log("[DEBUG] Fallback token loaded from localStorage:", decoded);
      } catch (err) {
        console.error("[DEBUG] Failed to decode stored token:", err);
      }
    } else {
      console.warn("[DEBUG] No token found in Redux or localStorage.");
    }
  } else {
    console.log("[DEBUG] Token found in Redux:", userToken);
  }

  const userName = userToken?.name
    ? userToken.name.replace(/^EXT:/, "")
    : "";
  console.log("[DEBUG] Final resolved userName:", userName);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const datasetIdParam = queryParams.get("c") || "";
  const chatIdParam = queryParams.get("cid") || "";
  const datasetNameParam = queryParams.get("title") || ""; // ✅ new line
  const agentIdFromUrl = queryParams.get("agentId");
  const isAgentScreen = !!agentIdFromUrl;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [toasterVisible, setToasterVisible] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterAppearance, setToasterAppearance] = useState("success");
  const messagesEndRef = useRef(null);
  const messagesDivRef = useRef(null);
  // Tracks whether we're waiting for a bot response so the message-change effect
  // can pin the question to the middle and avoid yanking the user during typewriter.
  const waitingForResponseRef = useRef(false);

  // Optional: keep one local chat created to satisfy "/chat" trigger
  const [localChatCreated, setLocalChatCreated] = useState(() => {
    // If cid exists in URL on mount (refresh case), chat is already created
    const urlCid = new URLSearchParams(window.location.search).get("cid");
    return !!urlCid;
  });
  const [chatMetaId, setChatMetaId] = useState(() => {
    // On refresh, initialize from cid in URL so saveConversation uses the correct chatId
    const urlCid = new URLSearchParams(window.location.search).get("cid");
    return urlCid || null;
  });
  const [lastQuestion, setLastQuestion] = useState("");
  const [likeMap, setLikeMap] = useState({}); // { conversationId: '1' | '0' }
  const [feedbackPending, setFeedbackPending] = useState(false);
  const [pendingConversation, setPendingConversation] = useState(null); // {question, answer}
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);
  const [showFeedbackOptions, setShowFeedbackOptions] = useState(false); // legacy inline path (kept off)
  const [showOtherFeedbackInput, setShowOtherFeedbackInput] = useState(false); // legacy inline path (kept off)
  const [feedbackInput, setFeedbackInput] = useState(""); // legacy inline path (kept off)
  const [selectedFeedbackOption, setSelectedFeedbackOption] = useState("");

  // Feedback modal (match normal flow)
  const [thubmsUpModel, setThubmsUpModel] = useState(false);
  const [radioValues, setRadioValues] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [suggestionOptions, setSuggestionOptions] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedSuggestionText, setSelectedSuggestionText] = useState("");

  const { suggestionData, suggestionStatus } = useSelector(
    (state) => state.feedbackSuggestionService || {}
  );

  const { chatHistoryData, registerChatStatus } = useSelector((state) => state.qnaChatHistory || {});
  const { feedbackServiceRequestStatus } = useSelector((state) => state.chatFeedbackService || {});
  const { successConversationData, successConversationDataStatus } = useSelector(
    (state) => state.saveSuccessChatConversation || {}
  );
  const { successAgentEmail, AgentEmailId } = useSelector((state) => state.getAgentEmail || {});

  // ✅ Add chat history Redux selector
  const { loading: chatHistoryLoading, chatData, chatDataStatus } = useSelector(
    (state) => state.ChatHistoryConversation || {}
  );

  // ✅ Add human agent Redux selectors
  const { agentData, error: createAgentError } = useSelector((state) => state.createAgent || {});
  const { statusData, agentStatus } = useSelector((state) => state.getAgentStatus || {});
  const { successAgentValidation, agentValid } = useSelector((state) => state.validateAgent || {});
  const { question, answer, type, questionTimestamp, answerTimestamp } = useSelector((state) => state.getHumanAgentQna || {});
  // Track last-handled per-field timestamp so identical message text sent
  // multiple times still produces a new render entry on user/agent screen.
  const lastHandledQuestionTsRef = useRef(null);
  const lastHandledAnswerTsRef = useRef(null);
  const { UserQuestion, AgentResponse, responseType } = useSelector((state) => state.createHumanAgentQna || {});

  // ✅ Added Redux parameters hook
  const { parameters, loading, error } = useSelector(
    (state) => state.parameters || {}
  );

  // ✅ Doc settings selector (serviceAgentEnabled)
  const { docSettings } = useSelector(
    (state) => state.fetchdocSettings || {}
  );

  // ✅ Add orgDetails selector for token usage tracking
  const orgDetails = useSelector((state) => state.admin.details);

  const [promptParams, setPromptParams] = useState({});
  const [DocSettingsvalue, setDocSettingsvalue] = useState({});

  // Image viewer state (Lightbox)
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentFilenames, setCurrentFilenames] = useState([]);

  // Source display state
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewDocUrl, setPreviewDocUrl] = useState("");
  const [previewDocFilename, setPreviewDocFilename] = useState("");
  const [previewPageNumber, setPreviewPageNumber] = useState(1);
  const [sourceActiveIndex, setSourceActiveIndex] = useState(1);

  // Local agent email tracking for external user
  const [agentEmailId, setAgentEmailId] = useState("");
  const [agentEmailAvailable, setAgentEmailAvailable] = useState(false);
  const [agentEmailFallbackTried, setAgentEmailFallbackTried] = useState(false);

  // ✅ Add human agent state variables
  const [AgentId, setAgentId] = useState(null);
  const [agentHelp, setAgentHelp] = useState(false);
  const [agentLeft, setAgentLeft] = useState(false);
  const [agentJoined, setAgentJoined] = useState(false);
  const [agentSessionTimedOut, setAgentSessionTimedOut] = useState(false);
  const [isAgentHelp, setIsAgentHelp] = useState(false);
  const [agentRequestFailed, setAgentRequestFailed] = useState(false);

  // If registering the live agent request fails, no request exists: tell the user and allow them to ask again.
  useEffect(() => {
    if (createAgentError) {
      setIsAgentHelp(false);
      setAgentRequestFailed(true);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          isQuestion: false,
          text: "We could not connect you to a live agent right now. Please try again.",
          isAgentMessage: true,
        },
      ]);
    }
  }, [createAgentError]);
  const [agentPollingInterval, setAgentPollingInterval] = useState(null);
  const [ValidatedAgentStatus, setValidatedAgentStatus] = useState("");
  const [QuestionText, setQuestionText] = useState("");
  const SESSION_DURATION = 3600; // 60 minutes = 3600 seconds
  const SESSION_TIMER_PREFIX = 'session_timer_'; // localStorage key prefix

  const [sessionTimer, setSessionTimer] = useState(null); // Timer in seconds (3600 = 60 mins)
  const [sessionStartTime, setSessionStartTime] = useState(null); // Timestamp when session started
  const [timerInterval, setTimerInterval] = useState(null);

  // Live Agent Help modal state (collect email and company name)
  const [showAgentHelpModal, setShowAgentHelpModal] = useState(false);
  const [agentHelpUserEmail, setAgentHelpUserEmail] = useState("");
  const [agentHelpCompanyName, setAgentHelpCompanyName] = useState("");
  const [agentHelpModalError, setAgentHelpModalError] = useState("");
  const [agentHelpConsentChecked, setAgentHelpConsentChecked] = useState(false);

  // ✅ Engagement tracking for ExternalUser - sends 10 secs every heartbeat
  const heartbeatIntervalRef = useRef(null);
  const messagesRef = useRef(messages);
  const agentPollingIntervalRef = useRef(null); // Ref to track interval for cleanup
  const agentIdRef = useRef(AgentId); // Ref to track latest AgentId for stale closure access

  // Sync agentPollingInterval to ref for cleanup
  useEffect(() => {
    agentPollingIntervalRef.current = agentPollingInterval;
  }, [agentPollingInterval]);

  // Sync AgentId to ref so handleSessionExpired always reads latest value
  useEffect(() => {
    agentIdRef.current = AgentId;
  }, [AgentId]);

  const timerIntervalRef = useRef(null); // Ref for timer interval
  const textareaRef = useRef(null); // Ref for auto-resize textarea

  // Sync timerInterval to ref for cleanup
  useEffect(() => {
    timerIntervalRef.current = timerInterval;
  }, [timerInterval]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Fetch organization details on mount for session tracking (maxQuestionsPerSession)
  useEffect(() => {
    dispatch(fetchOrganizationDetails());
  }, [dispatch]);

  // Add body class for scoping modal styles in ExternalUser context
  useEffect(() => {
    document.body.classList.add('external-user-body');
    return () => {
      document.body.classList.remove('external-user-body');
    };
  }, []);

  // ── localStorage helpers for per-chatId session timer ──
  const getTimerKey = (cid) => `${SESSION_TIMER_PREFIX}${cid}`;

  const saveTimerToStorage = (cid, startTime) => {
    if (!cid) return;
    try {
      localStorage.setItem(getTimerKey(cid), JSON.stringify({ startTime, durationSec: SESSION_DURATION }));
    } catch (e) { /* storage full / unavailable */ }
  };

  const getTimerFromStorage = (cid) => {
    if (!cid) return null;
    try {
      const raw = localStorage.getItem(getTimerKey(cid));
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  };

  const getRemainingFromStorage = (cid) => {
    const stored = getTimerFromStorage(cid);
    if (!stored) return null; // no record → new session
    const elapsed = Math.floor((Date.now() - stored.startTime) / 1000);
    return Math.max(stored.durationSec - elapsed, 0);
  };

  // Helper to handle session expiry
  const handleSessionExpired = (interval) => {
    setSessionTimer(0);
    // Use ref to get latest AgentId (avoids stale closure from useCallback([]))
    const targetAgentId = isAgentScreen ? agentIdFromUrl : agentIdRef.current;

    // Close agent status (token is preserved in localStorage so this won't 401)
    if (targetAgentId) {
      const payload = { status: 'Closed' };
      dispatch(humanAgentUpdateStatus(targetAgentId, payload));
    }

    setAgentSessionTimedOut(true);
    setAgentLeft(true);
    setIsAgentHelp(false);
    setAgentHelp(false);
    if (agentPollingIntervalRef.current) {
      clearInterval(agentPollingIntervalRef.current);
      setAgentPollingInterval(null);
    }
    if (interval) {
      clearInterval(interval);
    }
    setTimerInterval(null);
    setMessages((prevMessages) => {
      // Replace any existing loader ("Generating Response...") with the expiry message
      const filtered = prevMessages.filter(
        (msg) => msg.text !== "Generating Response..." && !msg.isAgentLoading && !msg.isUserLoading
      );
      return [
        ...filtered,
        {
          isQuestion: false,
          text: "Your session has expired. Please return to CorrigoPRO desktop and start a new chat session for further assistance.",
          isInitialGreeting: true,
        },
      ];
    });
    showToaster("Session expired. Please return to CorrigoPRO desktop.", "error", 30000);

    // Clear all localStorage keys except the auth token (kept briefly for the status update API)
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key !== "token") {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (e) { /* ignore */ }

    // Remove the auth token after 20 seconds so no further API calls can be made
    setTimeout(() => {
      try { localStorage.removeItem("token"); } catch (e) { /* ignore */ }
    }, 20000);

    // Strip all query string parameters from the URL so refresh goes to login
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (e) { /* ignore */ }
  };

  // Helper: start the countdown interval for the session timer
  const startTimerCountdown = useCallback((startTime) => {
    // Clear any existing timer interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    const interval = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      const remainingSeconds = SESSION_DURATION - elapsedSeconds;

      if (remainingSeconds <= 0) {
        clearInterval(interval);
        timerIntervalRef.current = null;
        handleSessionExpired(interval);
      } else {
        setSessionTimer(remainingSeconds);
      }
    }, 1000);

    setTimerInterval(interval);
    timerIntervalRef.current = interval;

    return interval;
  }, []);

  // ✅ Start session timer on page render.
  // If chatId exists in URL, resume/create from storage by chatId.
  // If chatId is not available yet, start immediately in-memory and persist once chatId is created.
  useEffect(() => {
    // Read cid directly from window.location since replaceState doesn't update React Router's location
    const urlParams = new URLSearchParams(window.location.search);
    const cidFromUrl = urlParams.get("cid") || chatIdParam;
    console.log("[SessionTimer] Mount check — chatIdParam:", chatIdParam, "window.location cid:", urlParams.get("cid"), "resolved:", cidFromUrl);

    if (cidFromUrl) {
      const remaining = getRemainingFromStorage(cidFromUrl);
      console.log("[SessionTimer] chatId from URL:", cidFromUrl, "remaining:", remaining);

      if (remaining !== null) {
        // Check if expired
        const stored = getTimerFromStorage(cidFromUrl);
        if (remaining <= 0) {
          console.log("[SessionTimer] Session expired for chatId:", cidFromUrl);
          handleSessionExpired(null);
          return;
        }
        setSessionStartTime(stored.startTime);
        setSessionTimer(remaining);
        console.log("[SessionTimer] Resuming session, remaining:", remaining, "seconds");
        const interval = startTimerCountdown(stored.startTime);
        return () => clearInterval(interval);
      }

      // chatId in URL but no timer record → first visit with this link, start fresh
      const startTime = Date.now();
      saveTimerToStorage(cidFromUrl, startTime);
      setSessionStartTime(startTime);
      setSessionTimer(SESSION_DURATION);
      console.log("[SessionTimer] New session for chatId:", cidFromUrl, "saved to localStorage");
      const interval = startTimerCountdown(startTime);
      return () => clearInterval(interval);
    }

    // No chatId yet: start timer immediately on page render
    const startTime = Date.now();
    setSessionStartTime(startTime);
    setSessionTimer(SESSION_DURATION);
    console.log("[SessionTimer] No chatId yet. Starting timer immediately on page render.");
    const interval = startTimerCountdown(startTime);
    return () => clearInterval(interval);
  }, []);

  // ✅ When chatId is registered after first question, persist timer to localStorage.
  // If timer somehow wasn't started yet, start it here.
  useEffect(() => {
    const newChatId = chatMetaId || chatHistoryData?.id;
    if (!newChatId) return;

    // If already persisted for this chat, nothing to do.
    if (getTimerFromStorage(newChatId)) return;

    if (sessionStartTime) {
      // Timer is already running from initial page render; persist same start time for this chatId.
      console.log("[SessionTimer] Chat registered, persisting existing timer start for chatId:", newChatId);
      saveTimerToStorage(newChatId, sessionStartTime);
      return;
    }

    // Fallback: if no timer exists yet, start now.
    const startTime = Date.now();
    console.log("[SessionTimer] Chat registered before timer init, starting timer for chatId:", newChatId);
    saveTimerToStorage(newChatId, startTime);
    setSessionStartTime(startTime);
    setSessionTimer(SESSION_DURATION);
    startTimerCountdown(startTime);
  }, [chatMetaId, chatHistoryData?.id, sessionStartTime]);

  // ✅ Component cleanup on unmount - reset all states and refs to prevent stale data
  useEffect(() => {
    return () => {
      // Clear intervals using refs (state may be stale in cleanup)
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (agentPollingIntervalRef.current) {
        clearInterval(agentPollingIntervalRef.current);
        agentPollingIntervalRef.current = null;
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      // Reset Redux agent states
      dispatch(resetUserQuestion());
      dispatch(resetAgentResponse());
      dispatch(resetQuestion());
      dispatch(resetAnswer());
    };
  }, []);

  // Track the previous datasetId so we only reset on actual dataset change, not on mount
  const prevDatasetIdRef = useRef(datasetIdParam);

  // ✅ Reset states when dataset changes (prevents stale data from previous session)
  useEffect(() => {
    if (datasetIdParam) {
      // On initial mount, skip the full reset so the page-render timer is not cleared.
      const isInitialMount = prevDatasetIdRef.current === datasetIdParam;

      if (isInitialMount) {
        // First render for this dataset — don't reset, just update the ref.
        prevDatasetIdRef.current = datasetIdParam;
        return;
      }

      prevDatasetIdRef.current = datasetIdParam;

      // Reset chat states
      setMessages([]);
      setLocalChatCreated(false);
      setChatMetaId(null);
      setLastQuestion("");
      setLikeMap({});
      setPromptParams({});

      // Reset agent states
      setAgentId(null);
      setAgentHelp(false);
      setAgentLeft(false);
      setAgentJoined(false);
      setIsAgentHelp(false);
      setAgentPollingInterval(null);
      setValidatedAgentStatus("");
      setQuestionText("");

      // Reset agent email states
      setAgentEmailId("");
      setAgentEmailAvailable(false);
      setAgentEmailFallbackTried(false);

      // Reset timer states
      setSessionTimer(null);
      setSessionStartTime(null);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        setTimerInterval(null);
      }

      // Reset refs - hasValidatedAgent is reset separately based on agentIdFromUrl
      messagesRef.current = [];
    }
  }, [datasetIdParam]);

  const showToaster = (message, appearance = "success", duration = 2500) => {
    setToasterMessage(message);
    setToasterAppearance(appearance);
    setToasterVisible(true);
    // Auto-close after duration if not manually closed
    setTimeout(() => {
      setToasterVisible(false);
    }, duration);
  };
  const closeToaster = () => setToasterVisible(false);

  // ✅ Fetch chat history function
  const fetchData = (chatId) => {
    try {
      console.log("[ExternalUser] Fetching chat history for chatId:", chatId);
      dispatch(getChatHistoryConversation(chatId));
    } catch (error) {
      console.error("[ExternalUser] Error fetching chat history:", error);
    }
  };

  // ✅ Fetch chat history when chatIdParam exists (for agent screen or page refresh)
  useEffect(() => {
    if (chatIdParam) {
      console.log("[ExternalUser] Fetching chat history for:", chatIdParam, "isAgentScreen:", isAgentScreen);
      fetchData(chatIdParam);
    }
  }, [chatIdParam]);

  // ✅ Process chat history data and populate messages (matching ChatBot.js)
  useEffect(() => {
    if (!chatData || !Array.isArray(chatData) || chatData.length === 0) {
      return;
    }

    console.log("[ExternalUser] Processing chat history data, length:", chatData.length);
    console.log("[ExternalUser] Raw chatData:", JSON.stringify(chatData.map(d => ({ q: d.userQuestion, a: d.botResponse?.substring(0, 50) }))));
    const newMessages = [];
    const greetingMessage = {
      isQuestion: false,
      text: "Hi, how may I help you today?",
      sourceData: [],
      imageBlobPaths: [],
      imageBlobURLs: [],
      chatConversationId: "",
      userQuestion: "",
      threadId: "",
      isInitialGreeting: true,
    };

    // Process chat history in order
    for (let i = 0; i < chatData.length; i++) {
      // On agent screen, skip both the question and bot response for "Agent-Help" entries
      if (isAgentScreen && chatData[i].userQuestion === 'Agent-Help') {
        continue;
      }
      // Add user question first — skip rows where the user side is empty
      // (human-agent reply rows have userQuestion="" and only the bot half).
      if (chatData[i].userQuestion) {
        // Transform "Agent-Help" to friendly message for display
        const displayText = chatData[i].userQuestion === 'Agent-Help'
          ? 'Live Agent Help requested.'
          : chatData[i].userQuestion;
        newMessages.push({
          isQuestion: true,
          text: displayText,
          sourceData: [],
          imageBlobPaths: [],
          imageBlobURLs: [],
          chatConversationId: chatData[i]._id || "",
          userQuestion: "",
          threadId: "",
          isFromChatHistory: true,
          timestamp:
            chatData[i].questionTimestamp ||
            chatData[i].createdUTC ||
            chatData[i].updatedUTC ||
            chatData[i].createdAt ||
            chatData[i].created_at ||
            chatData[i].timestamp ||
            null,
        });
      }
      // Then add bot response — skip rows where the bot/agent side is empty
      // (user-only rows from live-agent mode have botResponse="" and only the user half).
      if (chatData[i].botResponse) {
        newMessages.push({
          isQuestion: false,
          text: chatData[i].botResponse,
          sourceData: chatData[i].source || [],
          imageBlobPaths: chatData[i].imageBlobPaths || [],
          imageBlobURLs: chatData[i].imageBlobURLs || [],
          // These relative "images?path=..." entries came from chat-service
          // history, so authenticated fetches must use its API path.
          imageApiEndpoint: process.env.REACT_APP_CHAT_API_ENDPOINT,
          chatConversationId: chatData[i]._id || "",
          userQuestion: chatData[i].userQuestion || "",
          threadId: chatData[i].threadId || "",
          isRegenerate: chatData[i].isRegenerated || false,
          showImagesOption: chatData[i].showImagesOption || "fullPages",
          classification: chatData[i].classification || "",
          isFromChatHistory: true,
          // Human-agent reply rows have an empty userQuestion + non-empty botResponse; tag them so the timestamp pill renders after refresh.
          isAgentMessage: !chatData[i].userQuestion && !!chatData[i].botResponse,
          timestamp:
            chatData[i].answerTimestamp ||
            chatData[i].updatedUTC ||
            chatData[i].createdUTC ||
            chatData[i].updatedAt ||
            chatData[i].createdAt ||
            chatData[i].created_at ||
            chatData[i].timestamp ||
            null,
        });
      }
    }

    // Add greeting message at the beginning if not already present
    if (!newMessages.some((msg) => msg.isInitialGreeting)) {
      newMessages.unshift(greetingMessage);
    }

    console.log("[ExternalUser] Setting messages from chat history, count:", newMessages.length);
    setMessages(newMessages);
  }, [chatData, chatHistoryLoading, chatIdParam, chatDataStatus, userName]);

  // Stamp every newly-created message (user/bot/agent) with the local capture
  // time so each bubble can show when it occurred. History items keep the
  // backend-provided timestamp; greetings and loading placeholders are skipped.
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    let changed = false;
    const stamped = messages.map((m) => {
      if (
        m &&
        !m.timestamp &&
        !m.isInitialGreeting &&
        !m.isFromChatHistory &&
        !m.isAgentLoading &&
        m.text !== "Generating Response..."
      ) {
        changed = true;
        return { ...m, timestamp: new Date().toISOString() };
      }
      return m;
    });
    if (changed) setMessages(stamped);
  }, [messages]);

  // Derive a stable chat ID string so the effect only re-runs when the actual ID changes
  const activeChatId = chatMetaId || chatHistoryData?.id || chatIdParam;

  useEffect(() => {
    if (!activeChatId || !datasetIdParam || heartbeatIntervalRef.current) return;

    console.log("[Engagement] Starting tracking for chatId:", activeChatId);

    const sendEngagement = (isUserLeftChat = false) => {
      const userQuestions = messagesRef.current
        .filter(m => m.isQuestion && !m.isInitialGreeting)
        .map(m => m.text);

      return {
        userId: userToken?.sub || userToken?.oid || userToken?.email || "unknown",
        datasetId: datasetIdParam,
        chatId: activeChatId,
        timestamp: new Date().toISOString(),
        sessionDurationInSec: 10, // Fixed 10 secs per call
        metaData: {
          numOfQuestions: userQuestions.length,
          userQuestions: userQuestions,
          conversationType: "Short",
          isUserLeftChat,
          isExternalUser: true
        },
      };
    };

    // Every 10 seconds
    const intervalId = setInterval(() => {
      addEngagementAPI(sendEngagement(false))
        .then(() => console.log("[Engagement] Sent: 10 secs"))
        .catch(err => console.error("[Engagement] Failed:", err));
    }, 10000);

    heartbeatIntervalRef.current = intervalId;

    // Final save on tab close
    const handleBeforeUnload = () => {
      const url = `${process.env.REACT_APP_BASE_API_URL}/${process.env.REACT_APP_ENGAGEMENT_API_ENDPOINT}`;
      const blob = new Blob([JSON.stringify(sendEngagement(true))], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      addEngagementAPI(sendEngagement(true)).catch(() => { });
    };
  }, [activeChatId, datasetIdParam]);

  useEffect(() => {
    // Initial greeting
    if (messages.length === 0) {
      setMessages([
        {
          isQuestion: false,
          text: "Hi, how may I help you today?",
          isInitialGreeting: true,
        },
      ]);
    }
  }, [userName]);

  // ✅ Fetch dataset-specific prompt parameters via Redux (handled in API layer)
  useEffect(() => {
    if (datasetIdParam) {
      dispatch(fetchDataSetParameters(datasetIdParam));
      dispatch(fetchDocSettings(datasetIdParam));
    }
  }, [dispatch, datasetIdParam]);

  // ✅ Sync promptParams from Redux store
  // ✅ Sync promptParams from Redux store
  useEffect(() => {
    console.log("🧠 [DEBUG] parameters raw value from Redux:", parameters);
    console.log("🧠 [DEBUG] loading:", loading);
    console.log("🧠 [DEBUG] error:", error);

    const apiData = parameters?.data || parameters;

    if (apiData && Object.keys(apiData).length > 0 && !loading) {
      console.log("[DEBUG] Retrieved Dataset Parameters from API:", apiData);

      const mappedParams = {
        kValue: apiData.kValue,
        thresholdValue: apiData.thresholdValue,
        reRanker: apiData.enableReRanker,
        openAITemperature: apiData.temperature,
        maxOutputToken: apiData.maxTokens,
        topPValue: apiData.topPValue,
        modelName: apiData.modelName,
        retrievalStrategy: apiData.retrievalStrategy?.toLowerCase() || "hybrid",
        responseFormat: apiData.responseFormat?.toLowerCase() || "text",
        questionGenPrompt: apiData.questionGeneratorPrompt,
        clarifyPrompt: apiData.clarifyPrompt,
        regeneratePrompt: apiData.regeneratePrompt,
        regenerateWithFeedbackPrompt: apiData.regenerateWithFeedbackPrompt,
        showImagesOption: apiData.showImagesOption || "fullPages",
        showSource: apiData.showSource !== undefined ? apiData.showSource : true,
        systemPrompt: apiData.prompt,
        tableSchema: apiData.tableSchema || "",
        embeddingModel: apiData.embeddingModel || "text-embedding-ada-002",
        codeInterpreterEnabled: apiData.codeInterpreterEnabled || false,
        agenticQnAEnabled:
          apiData.agenticQnAEnabled === true ||
          apiData.agenticQnAEnabled === "true" ||
          apiData.agenticQnA === true ||
          apiData.agenticQnA === "true" ||
          false,
        agentPrompt: apiData.agentPrompt || "",
        // MCP Tools configuration
        mcpTools: apiData.mcpTools || [],
        selectedMcpTools: apiData.selectedMcpTools || [],
      };

      console.log("[DEBUG] ✅ Mapped Prompt Params used for QnA Payload:", mappedParams);
      setPromptParams((prev) => ({ ...prev, ...mappedParams }));
    }
  }, [parameters, loading, error]);

  // ✅ Store doc settings in dedicated state (matching ChatBot.js pattern)
  useEffect(() => {
    if (docSettings && Array.isArray(docSettings) && docSettings.length > 0) {
      const settings = docSettings[0];
      console.log("[DEBUG] Retrieved Doc Settings from API:", settings);
      setDocSettingsvalue(settings);
    }
  }, [docSettings]);

  // Ensure URL has cid and c for StarRating and consistency with normal flow
  // Also persist timer to localStorage once chatId is known
  useEffect(() => {
    if (registerChatStatus === 200 && chatHistoryData?.id) {
      try {
        const url = new URL(window.location);
        if (!url.searchParams.get("cid")) {
          url.searchParams.set("cid", chatHistoryData.id);
        }
        if (!url.searchParams.get("c")) {
          url.searchParams.set("c", datasetIdParam);
        }
        window.history.replaceState({}, document.title, url.toString());
      } catch (e) {
        // ignore URL errors
      }

      // Timer persistence is now handled by the chatMetaId/chatHistoryData useEffect above
    }
  }, [registerChatStatus, chatHistoryData, datasetIdParam]);

  // (Timer start on chat creation is handled by the chatMetaId/chatHistoryData effect above)

  // Load default feedback suggestions
  useEffect(() => {
    dispatch(getDefaultFeedbackSuggestion());
  }, [dispatch]);

  // Mirror normal flow: keep local suggestionOptions in sync
  useEffect(() => {
    if (suggestionData && suggestionStatus === 200) {
      setSuggestionOptions(suggestionData);
    } else {
      dispatch(getDefaultFeedbackSuggestion());
    }
  }, [suggestionData, suggestionStatus, dispatch]);

  // Agent email config (required for Agent-Help availability)
  useEffect(() => {
    // After chat loads: fetch configured agent email for dataset (from URL)
    dispatch(getAgentEmail(datasetIdParam));
  }, [dispatch, datasetIdParam]);

  // If the above GET returns empty (success=false), mirror normal flow fallback and try Email Support
  useEffect(() => {
    if (AgentEmailId) {
      setAgentEmailId(AgentEmailId);
      setAgentEmailAvailable(true);
      return;
    }

    if (!agentEmailFallbackTried && successAgentEmail !== true) {
      setAgentEmailFallbackTried(true);
      dispatch(getEmailSupport(datasetIdParam))
        .then((res) => {
          const toEmail = res?.data?.data?.toEmail;
          if (toEmail) {
            setAgentEmailId(toEmail);
            setAgentEmailAvailable(true);
          } else {
            const base = process.env.REACT_APP_EMAIL_SUPPORT_DOMAIN || "";
            if (base) {
              const domain = base.includes(".prod") ? base.replace(".prod", "") : base;
              const computed = `${domain}${datasetIdParam}@innoverdigital.com`;
              setAgentEmailId(computed);
              setAgentEmailAvailable(true);
            }
          }
        })
        .catch(() => { });
    }
  }, [successAgentEmail, AgentEmailId, dispatch, agentEmailFallbackTried]);

  // Scroll helper – only auto-scrolls when user is already near the bottom
  const scrollToBottom = useCallback(() => {
    const container = messagesDivRef.current;
    if (container) {
      const threshold = 180; // px from bottom to consider "near bottom"
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
      if (isNearBottom) {
        container.scrollTop = container.scrollHeight;
      }
    } else if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Image viewer helpers (matching ChatBot.js)
  const [viewerApiEndpoint, setViewerApiEndpoint] = useState(null);
  const openViewer = (filenames = [], imgIndex = 0, apiEndpoint = null) => {
    if (filenames.length === 0) return;
    setCurrentFilenames(filenames);
    setCurrentImageIndex(imgIndex);
    setViewerApiEndpoint(apiEndpoint);
    setIsViewerOpen(true);
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
  };

  // Auto-advance lightbox after opening (matching ChatBot.js)
  useEffect(() => {
    if (isViewerOpen && currentFilenames.length > 1) {
      const timer = setTimeout(() => {
        setCurrentImageIndex((currentImageIndex + 1) % currentFilenames.length);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isViewerOpen]);

  // Lightbox needs plain src strings (mainSrc/nextSrc/prevSrc are required
  // props of the third-party Lightbox component), so it resolves its own
  // authenticated fetches independently of the thumbnail grid — a redundant
  // fetch for whichever image was already shown as a thumbnail, traded for
  // not having to share fetched-image state across components.
  const [lightboxSrcs, setLightboxSrcs] = useState({ main: null, next: null, prev: null });
  useEffect(() => {
    if (!isViewerOpen || currentFilenames.length === 0) {
      return undefined;
    }
    const paths = {
      main: currentFilenames[currentImageIndex]?.trim(),
      next: currentFilenames[(currentImageIndex + 1) % currentFilenames.length]?.trim(),
      prev: currentFilenames[(currentImageIndex + currentFilenames.length - 1) % currentFilenames.length]?.trim(),
    };
    let cancelled = false;
    const createdUrls = [];
    setLightboxSrcs({ main: null, next: null, prev: null });
    Object.entries(paths).forEach(([slot, path]) => {
      if (!path || !viewerApiEndpoint) return;
      getImageBlob(path, viewerApiEndpoint)
        .then((blob) => {
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          createdUrls.push(objectUrl);
          setLightboxSrcs((prev) => ({ ...prev, [slot]: objectUrl }));
        })
        .catch((error) => {
          console.error("Failed to load lightbox image", path, error);
        });
    });
    return () => {
      cancelled = true;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [isViewerOpen, currentImageIndex, currentFilenames, viewerApiEndpoint]);

  useEffect(() => {
    // Mirror ChatBot.js scroll handling so the user can read responses from the
    // top of the answer while the typewriter renders.
    if (!messagesDivRef.current) return;
    const container = messagesDivRef.current;
    const lastMessage = messages[messages.length - 1];
    const isWaitingForResponse =
      lastMessage && !lastMessage.isQuestion && lastMessage.text === "Generating Response...";

    if (isWaitingForResponse) {
      // Mark that we're waiting — kept true until the typewriter completes
      waitingForResponseRef.current = true;
      // Remove any leftover padding from a previous question first
      container.style.paddingBottom = "";
      // Add extra bottom padding so the question can scroll to the middle
      container.style.paddingBottom = `${container.clientHeight * 0.5}px`;
      // Scroll to the very bottom — padding pushes the question into the middle
      container.scrollTop = container.scrollHeight;
    } else if (waitingForResponseRef.current) {
      // Response arrived — keep padding, don't scroll. Padding will be removed
      // by the typewriter onComplete handler after animation finishes.
    } else {
      // Normal case (loading chat history, greeting, etc.) — scroll to bottom
      container.style.paddingBottom = "";
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    // Scroll to bottom when feedback options or other feedback input appears
    if (showFeedbackOptions || showOtherFeedbackInput) {
      scrollToBottom();
    }
  }, [showFeedbackOptions, showOtherFeedbackInput, scrollToBottom]);

  useEffect(() => {
    if (!chatMetaId && registerChatStatus === 200 && chatHistoryData?.id) {
      setChatMetaId(chatHistoryData.id);
    }
  }, [registerChatStatus, chatHistoryData, chatMetaId]);

  useEffect(() => {
    if (chatMetaId && pendingConversation) {
      const { question, answer, responseData, isRegenerate } = pendingConversation;
      setPendingConversation(null);
      saveConversation(question, answer, responseData, isRegenerate);
    }
  }, [chatMetaId, pendingConversation]);

  useEffect(() => {
    if (feedbackPending && feedbackServiceRequestStatus === 200) {
      showToaster("Thanks for your feedback", "success");
      setFeedbackPending(false);
    }
  }, [feedbackPending, feedbackServiceRequestStatus]);

  const historyForPayload = useMemo(() => {
    const qs = [];
    const as = [];
    messages.forEach((m) => {
      if (m.isQuestion) qs.push(m.text || "");
      else as.push(m.text || "");
    });
    return {
      questions: qs.slice(-3),
      answers: as.slice(-3),
    };
  }, [messages]);

  // ✅ Updated buildQnaPayload to use promptParams
  // 🔹 Added fourth parameter `params` and replaced promptParams with params
  const buildQnaPayload = (question, userFeedback = " ", quickResponses = "", params = {}, isRegenerate = false) => {
    const questions = [...(historyForPayload.questions || []), question];
    const answers = [...(historyForPayload.answers || [])];

    const payload = {
      question,
      threadId: "",
      isRegenerate: isRegenerate,
      userFeedback,
      quickResponses,
      history: {
        questions: questions.slice(-3),
        answers: answers.slice(-3),
      },
      kValue: Number(params?.kValue) || 8,
      maxOutputToken: Number(params?.maxOutputToken) || 1024,
      thresholdValue: Number(params?.thresholdValue) || 0.4,
      reRanker: params?.reRanker || false,
      openAITemperature: Number(params?.openAITemperature) || 0.7,
      modelName: params?.modelName || "gpt-4o-mini",
      questionGenPrompt: params?.questionGenPrompt || "",
      clarifyPrompt: params?.clarifyPrompt || "",
      topPValue: Number(params?.topPValue) || 0.5,
      retrievalStrategy: params?.retrievalStrategy || "hybrid",
      responseFormat: params?.responseFormat || "text",
      regeneratePrompt: params?.regeneratePrompt || "",
      regenerateWithFeedbackPrompt: params?.regenerateWithFeedbackPrompt || "",
      showImagesOption: params?.showImagesOption || "fullPages",
      showSource: params?.showSource !== false,
      tableSchema: params?.tableSchema || "",
      embeddingModel: DocSettingsvalue?.embeddingModel || params?.embeddingModel || "text-embedding-ada-002",
      serviceAgentEnabled: DocSettingsvalue?.serviceAgentEnabled || params?.serviceAgentEnabled || false,
      // Lets qna-service know a live agent request is already pending
      liveAgentRequested: !!(isAgentHelp || agentHelp),
      systemPrompt: params?.systemPrompt || "",
      codeInterpreterEnabled: params?.codeInterpreterEnabled || false,
      agenticQnAEnabled: params?.agenticQnAEnabled || false,
      agentPrompt: params?.agentPrompt || "",
      // MCP Tools configuration for dataset-specific tools
      mcpTools: params?.mcpTools || [],
      selectedMcpTools: params?.selectedMcpTools || [],
    };

    return payload;
  };


  const buildCreateChatPayload = (title) => ({
    datasetId: datasetIdParam,
    datasetName: datasetNameParam || "Default Dataset", // ✅ dynamic
    title,
  });

  // ✅ Track token usage for EXT users (same as ChatBot.js)
  const trackTokenUsage = async (qnaResponseData, activeCid) => {
    try {
      // Check if token usage data exists in the response (handle both camelCase and snake_case)
      const tokenUsage = qnaResponseData?.tokenUsage || qnaResponseData?.token_usage;
      if (!tokenUsage) {
        console.log('[ExternalUser] No token usage data found in QnA response');
        return;
      }

      // Simple cost estimation based on model and token counts
      const calculateEstimatedCost = (model, promptTokens, completionTokens) => {
        // OpenAI pricing estimates (per 1K tokens)
        const pricingTable = {
          'gpt-3.5-turbo': { prompt: 0.0015, completion: 0.002 },
          'gpt-4': { prompt: 0.03, completion: 0.06 },
          'gpt-4o-mini': { prompt: 0.00015, completion: 0.0006 },
          'gpt-4o': { prompt: 0.0025, completion: 0.01 },
          'gpt-4.1-mini': { prompt: 0.0004, completion: 0.0016 },
          'gpt-4.1': { prompt: 0.002, completion: 0.008 },
          'gpt-5.1': { prompt: 0.003, completion: 0.012 },
          'gpt-5.2': { prompt: 0.003, completion: 0.012 },
          'gpt-5.4-mini': { prompt: 0.0011, completion: 0.0044 },
          'gpt-5.4': { prompt: 0.005, completion: 0.015 },
          'gpt-6-luna': { prompt: 0.0001, completion: 0 },
          'default': { prompt: 0.001, completion: 0.002 }
        };

        const pricing = pricingTable[model] || pricingTable['default'];
        const promptCost = (promptTokens / 1000) * pricing.prompt;
        const completionCost = (completionTokens / 1000) * pricing.completion;

        return promptCost + completionCost;
      };

      const modelName = tokenUsage.model || 'unknown';
      const promptTokens = tokenUsage.prompt_tokens || 0;
      const completionTokens = tokenUsage.completion_tokens || 0;
      const estimatedCost = calculateEstimatedCost(modelName, promptTokens, completionTokens);

      const userId = userToken?.oid || userToken?._id;

      // Extract essential token usage data for dashboard
      const tokenUsagePayload = {
        chatId: activeCid || chatIdParam || chatMetaId,
        datasetId: datasetIdParam,
        userId: userId || 'unknown',
        userName: userName || 'Unknown User',
        promptTokens: promptTokens,
        completionTokens: completionTokens,
        totalTokens: tokenUsage.total_tokens || 0,
        model: modelName,
        estimatedCost: estimatedCost,
        isRegenerated: qnaResponseData.isRegenerated || false,
        sourceCount: qnaResponseData.source?.length || 0,
        questionLength: qnaResponseData.userQuestion?.length || 0,
        responseLength: qnaResponseData.botResponse?.length || 0,
        // Usage settings from organization
        maxQuestionsPerSession: orgDetails?.maxQuestionsPerSession || 30,
        costPerSession: orgDetails?.costPerSession || 3.0,
        maxTokenUsagePerUserPerMonth: orgDetails?.maxTokenUsagePerUserPerMonth || 100000
      };

      console.log('[ExternalUser] Tracking token usage:', tokenUsagePayload);

      // Call the token usage API
      const response = await dispatch(saveTokenUsageAction(tokenUsagePayload));

      if (response?.data?.success) {
        console.log(`[ExternalUser] Token usage tracked successfully. Question #${response.data.questionNumber}, Session #${response.data.sessionNumber}`);
      } else {
        console.warn('[ExternalUser] Token usage tracking failed:', response?.data?.error || 'Unknown error');
      }

    } catch (error) {
      console.error('[ExternalUser] Error tracking token usage:', error);
      // Don't throw error to avoid breaking chat flow
    }
  };

  // ✅ Human agent helper functions
  const startAgentPolling = (agentId) => {
    const interval = setInterval(async () => {
      try {
        dispatch(humanAgentGetStatus(agentId));
      } catch (error) {
        console.error("Error fetching agent status:", error);
      }
    }, 6000); // Poll every 6 seconds

    setAgentPollingInterval(interval);
    return () => clearInterval(interval);
  };

  const checkAgentJoined = async (agentId) => {
    try {
      dispatch(humanAgentValidate(agentId));
      dispatch(humanAgentGetStatus(agentId)); // Also fetch status to determine if closed vs active
      setValidatedAgentStatus(agentValid);
    } catch (error) {
      console.error("Error validating agent:", error);
    }
  };

  const handleAgentLeft = () => {
    clearInterval(agentPollingInterval);
    setAgentPollingInterval(null);
    setAgentId(null);
    setAgentHelp(false);
    setIsAgentHelp(false);
    setAgentLeft(true);
    // Reset Redux agent states to prevent stale responses from appearing after timeout
    dispatch(resetAnswer());
    dispatch(resetAgentResponse());
    dispatch(resetQuestion());
    dispatch(resetUserQuestion());
    showToaster("Agents are busy. Support ticket will get created for you.", "error");
    setMessages((prevMessages) => {
      // Remove the loader and add the timeout message in its place
      const filtered = prevMessages.filter(msg => msg.isAgentLoading !== true);
      return [
        ...filtered,
        {
          isQuestion: false,
          text: "The support agents are busy. A support ticket has been created, and an agent will reach out to you shortly.",
          isAgentSystemMessage: true,
        },
      ];
    });
  };

  const handleUserQuestion = async (questionText, agentIdOverride = null) => {
    if (questionText === 'Quit Agent') {
      const payload = {
        status: 'Closed'
      };
      dispatch(humanAgentUpdateStatus(AgentId, payload));
      setIsAgentHelp(false);
      setAgentHelp(false);
      setAgentId(null);
      setAgentLeft(true);
      showToaster("Live Agent Help disabled", "success");
      clearInterval(agentPollingInterval);
      setAgentPollingInterval(null);
      // Remove any existing agent loader from messages
      setMessages((prev) => prev.filter(msg => msg.isAgentLoading !== true));
      return;
    }
    try {
      // Add only the user message in agent mode; avoid placeholder bubbles while waiting.
      if (questionText !== 'Agent-Help' && questionText !== 'Quit Agent') {
        setMessages((prevMessages) => [
          ...prevMessages,
          { isQuestion: true, text: questionText },
        ]);
      }
      const targetAgentId = agentIdOverride || AgentId;
      const payload = {
        question: questionText,
        chatId: chatIdParam || chatMetaId,
        datasetId: datasetIdParam
      };
      await dispatch(humanAgentQna(targetAgentId, 'question', payload));
    } catch (error) {
      console.error("Error sending question to agent:", error);
    }
  };

  const handleAgentAnswer = async (answerText) => {
    if (answerText === 'Quit Agent') {
      if (agentPollingInterval) {
        clearInterval(agentPollingInterval);
        setAgentPollingInterval(null);
      }
      const payload = {
        status: 'Closed'
      };
      setQuestionText('Quit Agent');
      dispatch(humanAgentUpdateStatus(agentIdFromUrl, payload))
        .then(() => {
          setIsAgentHelp(false);
          setAgentHelp(false);
          setAgentId(null);
          setAgentLeft(true);
          showToaster("Live Agent Help disabled", "success");
          setTimeout(() => trigger('/'), 1500);
        });
    }
    try {
      // Add only the agent's answer; avoid placeholder bubbles while waiting.
      if (answerText !== 'Quit Agent' && !['Connecting you to Live Chat Support now...Please wait.', "We have notified the Support team!\nSomeone will be with you shortly", 'Please Configure Agent Email to connect to Live Chat Support.', 'Support agent joined the chat.', "Hey! I'm here to help. I'm just catching up on the conversation so far to understand the issue. Give me just a moment and I'll be ready to help!"].includes(answerText)) {
        setMessages((prevMessages) => [
          ...prevMessages,
          { isQuestion: true, text: answerText },
        ]);
      }
      const payload = {
        answer: answerText,
        chatId: chatIdParam || chatMetaId,
        datasetId: datasetIdParam
      };
      await dispatch(humanAgentQna(agentIdFromUrl, 'answer', payload));
    } catch (error) {
      console.error("Error sending answer to user:", error);
    }
  };

  const doAsk = async (question, isRegenerate = false, userFeedback = " ") => {
    if (!question) return;

    // reset satisfaction flags for new response
    setShowThankYouMessage(false);
    setShowFeedbackOptions(false);
    setShowOtherFeedbackInput(false);
    setFeedbackInput("");

    setMessages((prev) => [
      ...prev,
      ...(isRegenerate ? [] : [{ isQuestion: true, text: question }]),
      { isQuestion: false, text: "Generating Response..." },
    ]);
    setLastQuestion(question);
    setIsLoading(true);

    try {
      if (!localChatCreated) {
        const payload = buildCreateChatPayload(question);
        await dispatch(createChatHistoryData(payload));
        setLocalChatCreated(true);
      }

      // Ensure we have a chat id (from URL or newly created chat)
      const waitForChatId = async (ms = 2000, step = 100) => {
        const start = Date.now();
        while (!chatMetaId && !new URLSearchParams(window.location.search).get("cid") && Date.now() - start < ms) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, step));
        }
      };
      if (!chatMetaId && !chatIdParam) {
        await waitForChatId();
      }
      const activeCid = chatMetaId || new URLSearchParams(window.location.search).get("cid") || chatIdParam || "";

      const qnaPayload = buildQnaPayload(question, userFeedback, "", promptParams, isRegenerate);
      console.log("[DEBUG] Retrieved Prompt Params used for QnA Payload:", promptParams);
      console.log("[DEBUG] Final QnA Payload:", qnaPayload);
      const response = await getQnaChatbotData(qnaPayload, datasetIdParam, activeCid);

      const data = response?.data || {};
      const botResponse = data.botResponse || data.answer || "Unable to provide an answer at this time, please try again.";

      const chatMeta = data.chatMeta || {};

      setMessages((prev) => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (!updated[i].isQuestion && updated[i].text === "Generating Response...") {
            updated[i] = {
              isQuestion: false,
              text: botResponse,
              sourceData: data.source || [],
              imageBlobPaths: chatMeta.imageBlobPaths || data.imageBlobPaths || [],
              imageBlobURLs: chatMeta.imageBlobURLs || data.imageBlobURLs || [],
              // These relative "images?path=..." entries came from a live
              // qna-service answer, so authenticated fetches must use its API path.
              imageApiEndpoint: process.env.REACT_APP_QUERY_API_ENDPOINT,
              threadId: chatMeta.threadId || data.threadId || "",
              isRegenerate: isRegenerate,
              showImagesOption: chatMeta.showImagesOption || promptParams?.showImagesOption || "fullPages",
              classification: chatMeta.classification || data.classification || "",
            };
            break;
          }
        }
        return updated;
      });

      // qna-service detected a live agent request: open the same modal as "Agent-Help"
      if (chatMeta.liveAgentRequest) {
        handleAgentHelp();
      }

      saveConversation(question, botResponse, { ...data, ...chatMeta }, isRegenerate);

      // ✅ Track token usage for EXT users (so sessions appear in dashboard)
      trackTokenUsage(data, activeCid);
    } catch (err) {
      console.error("ExternalUser QnA error:", err);
      showToaster("Failed to get response. Please try again.", "error");
      setMessages((prev) => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (!updated[i].isQuestion && updated[i].text === "Generating Response...") {
            updated[i] = { isQuestion: false, text: "Unable to provide an answer at this time, please try again." };
            break;
          }
        }
        return updated;
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Build chat history for Agent-Help registration (prefer persisted chatData)
  const buildAgentHistory = () => {
    if (Array.isArray(chatData) && chatData.length) {
      const qs = [];
      const ans = [];
      chatData.forEach((c) => {
        if (typeof c.userQuestion === "string") qs.push(c.userQuestion);
        if (typeof c.botResponse === "string") ans.push(c.botResponse);
      });
      return { qs, ans };
    }

    // Fall back to in-memory messages
    const qs = [];
    const ans = [];
    messages.forEach((m) => {
      if (m.isInitialGreeting) return;
      if (m.isQuestion) {
        qs.push(m.text || "");
      } else {
        if (m.userQuestion) qs.push(m.userQuestion);
        ans.push(m.text || "");
      }
    });
    return { qs, ans };
  };

  // Agent-Help integration - shows modal to collect email and company name first
  const handleAgentHelp = () => {
    // A request is already pending (isAgentHelp) or the agent has joined (agentHelp):
    // do not open the form again, which would create a second request.
    if (isAgentHelp || agentHelp) {
      setMessages((prev) => [
        ...prev,
        {
          isQuestion: false,
          text: "A live agent request is already in progress. A team member will join you shortly.",
          isAgentMessage: true,
        },
      ]);
      return;
    }
    if (!agentEmailAvailable) {
      setMessages((prev) => [
        ...prev,
        { isQuestion: false, text: "Please Configure Agent Email to connect to Live Chat Support." },
      ]);
      showToaster("Agent Email not configured.", "error");
      return;
    }
    // Show modal to collect user email and company name
    setAgentHelpUserEmail("");
    setAgentHelpCompanyName("");
    setAgentHelpModalError("");
    setAgentHelpConsentChecked(false);
    setShowAgentHelpModal(true);
  };

  // Close agent help modal
  const handleAgentHelpModalClose = () => {
    setShowAgentHelpModal(false);
    setAgentHelpUserEmail("");
    setAgentHelpCompanyName("");
    setAgentHelpModalError("");
    setAgentHelpConsentChecked(false);
  };

  // Process agent help request after collecting email and company name
  const handleAgentHelpSubmit = async () => {
    // Clear previous errors
    setAgentHelpModalError("");

    // Validate email
    if (!agentHelpUserEmail || !agentHelpUserEmail.trim()) {
      setAgentHelpModalError("Please enter your email address.");
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(agentHelpUserEmail.trim())) {
      setAgentHelpModalError("Please enter a valid email address.");
      return;
    }
    // Validate company name
    if (!agentHelpCompanyName || !agentHelpCompanyName.trim()) {
      setAgentHelpModalError("Please enter your company name.");
      return;
    }

    try {
      setShowAgentHelpModal(false);
      // Clear the previous live-agent session so the badge doesn't show "Joined" from the old agent
      dispatch(resetAgentStatus());
      setAgentJoined(false);
      setIsAgentHelp(true);
      setAgentLeft(false);
      setAgentRequestFailed(false);
      // Add "Live Agent Help requested" message immediately
      setMessages((prev) => [
        ...prev,
        { isQuestion: true, text: "Live Agent Help requested." },
      ]);
      // Show connecting message after 1 second delay
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { isQuestion: false, text: 'Connecting you to Live Chat Support now...Please wait.', isAgentMessage: true },
        ]);
      }, 2200);
      showToaster("Live Agent Help request sent", "success");

      const { qs, ans } = buildAgentHistory();

      // 1) Register agent with email and company name
      const registerPayload = {
        datasetId: datasetIdParam,
        datasetTitle: datasetNameParam || "Default Dataset",
        chatId: chatIdParam || chatMetaId,
        userName: userName,
        chatHistoryQuestions: qs,
        chatHistoryAnswers: ans,
        userEmail: agentHelpUserEmail.trim(),
        companyName: agentHelpCompanyName.trim(),
      };
      const reg = await dispatch(humanAgentRegister(registerPayload));
      const agentId = reg?.agentId || reg?.data?.agentId || reg?.id;

      if (agentId) {
        setAgentId(agentId);
        // Update human-in-loop refs in token usage service for session dashboard
        const chatId = chatIdParam || chatMetaId;
        if (chatId) {
          updateHumanInLoopRefs(chatId, agentId)
            .then((res) => {
              if (res?.data?.success) {
                console.log('[ExternalUser] Human-in-loop ref added:', agentId);
              }
            })
            .catch((err) => console.error('[ExternalUser] Failed to update human-in-loop refs:', err));
        }
        await handleUserQuestion("Agent-Help", agentId);
      } else {
        // Fallback: proceed with existing flow
        await handleUserQuestion("Agent-Help");
      }
      // Clear the form
      setAgentHelpUserEmail("");
      setAgentHelpCompanyName("");
    } catch (err) {
      console.error("Live Agent Help flow failed", err);
      showToaster("Failed to initiate Live Agent Help.", "error");
    }
  };

  const handleSendQuestion = async (questionText) => {
    // Ensure questionText is a string
    if (!questionText || typeof questionText !== 'string') return;

    const trimmedQuestion = questionText.trim();
    if (!trimmedQuestion) return;

    if (trimmedQuestion.includes("Agent-Help")) {
      if (DocSettingsvalue?.serviceAgentEnabled && agentEmailAvailable) {
        try {
          await handleAgentHelp();
        } catch (e) {
          console.error('Agent registration failed', e);
        }
      } else if (trimmedQuestion.includes("Agent-Help")) {
        setMessages((prev) => [
          ...prev,
          { isQuestion: false, text: "Please Configure Agent Email to connect to Live Chat Support." },
        ]);
        showToaster("Agent Email not configured.", "error");
      }
    } else if (isAgentScreen && agentValid !== false) {
      handleAgentAnswer(trimmedQuestion);
    } else if (agentHelp && !isAgentScreen) {
      handleUserQuestion(trimmedQuestion);
    } else {
      await doAsk(trimmedQuestion);
    }
  };

  const sendQuestion = async (questionOverride = null) => {
    // If questionOverride is an event object (has target property), ignore it
    const isEvent = questionOverride && typeof questionOverride === 'object' && 'target' in questionOverride;

    let question;
    if (isEvent || !questionOverride) {
      // Use input field value
      question = (input || "").trim();
      setInput("");
      // Reset textarea height after clearing input
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } else if (typeof questionOverride === 'string') {
      // Use provided string
      question = questionOverride.trim();
    } else {
      return;
    }

    if (!question) return;

    await handleSendQuestion(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  const saveConversation = async (question, answer, responseData = {}, isRegenerate = false) => {
    try {
      // If chatMetaId isn't ready yet, wait a tick; otherwise try later when available
      if (!chatMetaId) {
        setPendingConversation({ question, answer, responseData, isRegenerate });
        return;
      }

      const payload = {
        userQuestion: question,
        botResponse: answer,
        source: responseData.source || [],
        isRegenerated: isRegenerate,
        showImagesOption: promptParams?.showImagesOption || "fullPages",
        showSource: promptParams?.showSource !== undefined ? promptParams.showSource : true,
        docSettingsVersion: String(DocSettingsvalue?.version || "0"),
        qnaSettingsVersion: String(promptParams?.version || "0"),
        qnaSettingsId: promptParams?._id || "",
        docSettingsId: DocSettingsvalue?._id || "",
        imageBlobPaths: responseData.imageBlobPaths || [],
        imageBlobURLs: responseData.imageBlobURLs || [],
        threadId: responseData.threadId || "",
      };
      await dispatch(SaveHistoryConversation(payload, chatMetaId));
    } catch (e) {
      console.error("Save conversation failed", e);
    }
  };

  // After SaveHistoryConversation success, attach chatConversationId to the latest answer
  useEffect(() => {
    if (successConversationData && successConversationDataStatus === 200) {
      const conversationId = successConversationData.id;
      if (!conversationId) return;
      setMessages((prev) => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (!updated[i].isQuestion && !updated[i].chatConversationId) {
            updated[i] = {
              ...updated[i],
              chatConversationId: conversationId,
              userQuestion: lastQuestion,
            };
            break;
          }
        }
        return updated;
      });
    }
  }, [successConversationData, successConversationDataStatus, lastQuestion]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      showToaster("Successfully copied to clipboard", "success");
    } catch (err) {
      console.error("Failed to copy text", err);
      showToaster("Failed to copy", "error");
    }
  };

  // Handler for opening thumbs down modal
  const handleThubmsModel = (conversationId) => {
    setSelectedConversationId(conversationId);
    setThubmsUpModel(true);
  };

  const handleLikeDislike = (conversationId, userQuestion, aiResponse, likeType) => {
    if (!conversationId) {
      showToaster("Please ask a question first", "warning");
      return;
    }
    const payload = {
      datasetId: datasetIdParam,
      chatMetaId: chatMetaId,
      chatConversationId: conversationId,
      likeType, // 1 for like, 0 for dislike
      suggestionType: "",
      reason: "",
      datasetTitle: datasetNameParam || "External Chat",
      userQuestion: userQuestion || lastQuestion,
      feedbackType: "",
      aiResponse: aiResponse || "",
    };
    setFeedbackPending(true);
    dispatch(feedbackServiceRequest(payload));
    setLikeMap((prev) => ({ ...prev, [conversationId]: String(likeType) }));
  };

  const handleSatisfaction = (isSatisfied) => {
    // target the last bot message
    const lastBot = [...messages].reverse().find((m) => !m.isQuestion && m.text && m.text !== "Generating Response...");
    if (!lastBot) return;
    if (isSatisfied) {
      handleLikeDislike(lastBot.chatConversationId, lastBot.userQuestion, lastBot.text, 1);
      setShowThankYouMessage(true);
      setShowFeedbackOptions(false);
      setShowOtherFeedbackInput(false);
    } else {
      // Show inline feedback options (consistent with ChatBot.js behavior)
      setSelectedConversationId(lastBot.chatConversationId);
      setShowFeedbackOptions(true);
      setShowThankYouMessage(false);
      setSelectedFeedbackOption("");
    }
  };

  // Helpers for modal behavior (align with normal flow)
  const isEmpty = (value) => value == null || (typeof value === "string" && value.trim().length === 0);
  const handleSelectRadio = (event, { value }) => {
    setRadioValues({ value });
    setIsButtonEnabled(true);
  };
  const handleTableRowClick = (value, text) => {
    setRadioValues({ value });
    setSelectedSuggestionText(text);
    setIsButtonEnabled(true);
  };
  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    !isEmpty(val) || (radioValues && radioValues.value)
      ? setIsButtonEnabled(true)
      : setIsButtonEnabled(false);
  };
  const handleClose = () => {
    setThubmsUpModel(false);
    setRadioValues(null);
    setSelectedSuggestionText("");
    setInputValue("");
    setIsButtonEnabled(false);
  };
  const handleThubmsUpPopup = async () => {
    if (!isButtonEnabled || !selectedConversationId) return;
    try {
      const payload = {
        datasetId: datasetIdParam,
        chatMetaId: chatMetaId,
        chatConversationId: selectedConversationId,
        likeType: 0,
        suggestionType: selectedSuggestionText || "",
        reason: inputValue,
        datasetTitle: datasetNameParam || "External Chat",
        userQuestion: lastQuestion,
        feedbackType: "dislike",
        aiResponse: messages.find((m) => m.chatConversationId === selectedConversationId)?.text || "",
      };
      setFeedbackPending(true);
      await dispatch(feedbackServiceRequest(payload));
      showToaster("Thanks for your feedback", "success");
    } finally {
      handleClose();
    }
  };

  const handleFeedbackOptionClick = (optionTitle) => {
    const lastBot = [...messages].reverse().find((m) => !m.isQuestion && m.text && m.text !== "Generating Response...");
    if (!lastBot) return;
    setSelectedFeedbackOption(optionTitle);
    setShowOtherFeedbackInput(true);
  };

  const submitOtherFeedback = () => {
    const lastBot = [...messages].reverse().find((m) => !m.isQuestion && m.text && m.text !== "Generating Response...");
    if (!lastBot) return;
    const payload = {
      datasetId: datasetIdParam,
      chatMetaId: chatMetaId,
      chatConversationId: lastBot.chatConversationId,
      likeType: 0,
      suggestionType: selectedFeedbackOption || "Other",
      reason: feedbackInput,
      datasetTitle: datasetNameParam || "External Chat",
      userQuestion: lastBot.userQuestion || lastQuestion,
      feedbackType: "feedback",
      aiResponse: lastBot.text || "",
    };
    setFeedbackPending(true);
    dispatch(feedbackServiceRequest(payload));
    // Update likeMap to disable Yes/No options
    setLikeMap((prev) => ({ ...prev, [lastBot.chatConversationId]: "0" }));
    setShowThankYouMessage(false);
    setShowFeedbackOptions(false);
    setShowOtherFeedbackInput(false);
    const feedbackText = feedbackInput;
    setFeedbackInput("");
    setSelectedFeedbackOption("");
    // Regenerate response with feedback
    const questionText = lastBot.userQuestion || lastQuestion;
    if (questionText) {
      doAsk(questionText, true, feedbackText);
    }
  };

  const handleRegenerate = () => {
    const target = lastQuestion;
    if (!target) return;
    doAsk(target, true);
  };

  // Source display: download and preview handlers.
  // The storage account backing dataset documents has public network access
  // disabled, so a SAS URL is unreachable directly from the browser (403
  // AuthorizationFailure). These fetch the bytes through an authenticated
  // backend route instead, same pattern as the AuthenticatedImage/getImageBlob
  // fix for chat-embedded images.
  const handleDownloadChatFile = async (datasetId, filename) => {
    try {
      const blob = await getFileContentBlob(datasetId, filename);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      showToaster("Error downloading file", "error");
    }
  };

  const previewObjectUrlRef = useRef(null);

  const handlePreview = (datasetId, filename, pageNumber) => async () => {
    setPreviewDocFilename(filename);
    setPreviewPageNumber(pageNumber);
    try {
      const blob = await getFileContentBlob(datasetId, filename);
      if (previewObjectUrlRef.current) {
        window.URL.revokeObjectURL(previewObjectUrlRef.current);
      }
      const objectUrl = window.URL.createObjectURL(blob);
      previewObjectUrlRef.current = objectUrl;
      setPreviewDocUrl(objectUrl);
      setIsPreviewModalOpen(true);
    } catch (error) {
      console.error("Error previewing file:", error);
      showToaster("Error downloading file", "error");
    }
  };

  const handleClosePreviewModal = () => {
    setIsPreviewModalOpen(false);
  };

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        window.URL.revokeObjectURL(previewObjectUrlRef.current);
      }
    };
  }, []);

  const handleSourceAccordionClick = (e, titleProps) => {
    const { index } = titleProps;
    setSourceActiveIndex(sourceActiveIndex === index ? -1 : index);
  };

  // Render source cards (matching ChatBot.js)
  const renderCards = (sourceData) => {
    if (!sourceData || sourceData.length === 0) {
      return null;
    }

    return (
      <Grid columns={3}>
        {sourceData.map((data, index) => {
          const filename = data.source.split("/").pop();
          const pages = data.page;
          const pageNumbers = pages.split(",").map(Number);
          const sourceDatasetId = data.source.split("/")[0];
          const uniquePages = [...new Set(pageNumbers)];

          const chunkedPages = [];
          for (let i = 0; i < uniquePages.length; i += 7) {
            chunkedPages.push(uniquePages.slice(i, i + 7));
          }

          return (
            <Grid.Column key={index} className="question-box-outer">
              <Card style={{ border: "1px solid #ccc" }}>
                <Card.Content className="question-box-inner">
                  <Card.Meta>
                    <p
                      style={{
                        fontSize: "15px",
                        fontWeight: "500",
                        color: "#36475a",
                      }}
                    >
                      {filename.length > 13 ? (
                        <Popup
                          content={filename}
                          inverted
                          position="right center"
                          style={{ borderRadius: "15px" }}
                          trigger={<label>{filename.slice(0, 13) + ".."}</label>}
                        />
                      ) : (
                        filename
                      )}
                    </p>
                    <div style={{ paddingTop: "10px" }} className="greycolor">
                      Pages:
                      <img
                        onClick={handlePreview(sourceDatasetId, filename, 1)}
                        src="/images/preview-grey-icon.svg"
                        alt="Preview"
                      />
                      <img
                        onClick={() =>
                          handleDownloadChatFile(sourceDatasetId, filename)
                        }
                        src="/images/Download.svg"
                        alt="pdf Icon"
                        size="mini"
                        data-cy="ai-assitant-source-download"
                        style={{
                          marginLeft: "10px",
                          cursor: "pointer",
                          float: "right",
                        }}
                      />
                    </div>
                    <div style={{ marginTop: "5px" }}>
                      {chunkedPages.map((chunk, chunkIndex) => (
                        <div key={chunkIndex} style={{ marginBottom: "5px" }}>
                          {chunk.map((page) => (
                            <span
                              key={page}
                              onClick={handlePreview(sourceDatasetId, filename, page)}
                              style={{
                                marginRight: "10px",
                                cursor: "pointer",
                                color: "blue",
                                textDecoration: "underline",
                              }}
                            >
                              {page}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  </Card.Meta>
                </Card.Content>
              </Card>
            </Grid.Column>
          );
        })}
      </Grid>
    );
  };

  // ✅ Human agent useEffect hooks

  // Start agent polling when agent is registered
  useEffect(() => {
    if (agentData && isAgentHelp && AgentId) {
      const agentId = agentData.agentId;
      setAgentId(agentId);
      startAgentPolling(AgentId);
    }
    // Cleanup function to stop polling when agent leaves or component unmounts
    return () => {
      if (agentPollingInterval) {
        clearInterval(agentPollingInterval);
        setAgentPollingInterval(null);
      }
    };
  }, [agentData, AgentId, isAgentHelp]);

  // Validate agent when agentIdFromUrl is present
  const hasValidatedAgent = useRef(false);
  // Guard so the "{userName} left the chat." notice is appended only once
  // even if the question poll keeps returning 'Quit Agent' before validate
  // flips agentValid to false.
  const userQuitNoticeShownRef = useRef(false);

  // Reset hasValidatedAgent when agentIdFromUrl changes
  useEffect(() => {
    // Reset the ref when agentIdFromUrl changes or is removed
    hasValidatedAgent.current = false;
  }, [agentIdFromUrl]);

  useEffect(() => {
    if (agentIdFromUrl && !hasValidatedAgent.current) {
      checkAgentJoined(agentIdFromUrl);
      hasValidatedAgent.current = true;
    }
  }, [agentIdFromUrl]);

  // Handle agent valid/invalid status
  useEffect(() => {
    if (agentValid === undefined || agentValid === null) return;

    if (agentValid === false && isAgentScreen) {
      if (agentSessionTimedOut || sessionTimer === 0) {
        // Session expired via timer - don't redirect, let agent see the expiry message
        showToaster("Session has expired.", "error");
        return;
      }
      if (QuestionText !== 'Quit Agent' && agentStatus !== 'Closed') {
        showToaster("Someone has already joined the chat.", "error");
        const queryParams = new URLSearchParams(location.search);
        queryParams.delete("agentId");
        queryParams.delete("cid");
        queryParams.delete("c");
        queryParams.delete("title");
        const newUrl = `${location.pathname}?${queryParams.toString()}`;
        trigger(newUrl);
        setMessages([]);
      } else if (agentStatus === 'Closed') {
        // Session ended (user clicked Stop Live Agent or quit). Preserve the
        // conversation on the agent screen so the agent can still review it.
        showToaster("User has ended the session.", "error");
      }
    } else if (agentValid === true && isAgentScreen) {
      // Check if session already closed (user timed out before agent joined,
      // or the agent ended the session themselves before validate returned).
      if (agentStatus === 'Closed') {
        showToaster("Agent session duration timeout", "error");
        // Keep messages intact so the agent can still review the conversation.
      } else if (agentStatus) {
        // Only enable if status is not Closed and status is available
        if (userRole === 'User') {
          setMessages([]);
        }
        showToaster(`Live Agent Help enabled`, "success");
      }
    }
  }, [agentValid, agentStatus]);

  // Set agentJoined when agent status is Active (timer already started on mount)
  useEffect(() => {
    if (!agentStatus) return;

    if (agentStatus === 'Active' && !agentJoined) {
      setAgentJoined(true);
    }
  }, [agentStatus, agentJoined]);

  // Clear title-flash + favicon-dot once the session actually ends.
  useEffect(() => {
    if (agentLeft || agentSessionTimedOut || agentStatus === 'Closed') {
      clearAgentNotifications();
    }
  }, [agentLeft, agentSessionTimedOut, agentStatus]);

  // Safety net: clear on unmount only (do NOT depend on session vars,
  // otherwise the cleanup runs on every dep change and kills the flash).
  useEffect(() => () => clearAgentNotifications(), []);

  // Handle agent status changes (Active, Open, Closed)
  useEffect(() => {
    if (!agentStatus) return;

    if (agentStatus === 'Active' && isAgentHelp && AgentId !== null && !agentLeft) {
      showToaster("Agent Joined the chat", "success");
      setAgentHelp(true);  // Enable agent help to start polling for answers
      if (!isAgentScreen) {
        notifyAgentEvent("agent");
        // Show messages locally only - don't send to backend to avoid agent screen duplicates
        setMessages((prev) => [
          ...prev,
          { isQuestion: false, text: "Support agent joined the chat.", isAgentMessage: true },
        ]);
        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            { isQuestion: false, text: "Hey! I'm here to help. I'm just catching up on the conversation so far to understand the issue. Give me just a moment and I'll be ready to help!", isAgentMessage: true },
          ]);
        }, 2000);
      }
    } else if (agentStatus === "Open" && isAgentHelp) {
      // Show second message after a brief delay (first message already shown on submit)
      setTimeout(() => {
        setMessages((prevMessages) => [
          ...prevMessages,
          { isQuestion: false, text: "We have notified the Support team!\nSomeone will be with you shortly.", isAgentMessage: true },
        ]);
      }, 1500);
      const timer = setTimeout(() => {
        // Set status to Closed first
        const payload = { status: 'Closed' };
        dispatch(humanAgentUpdateStatus(AgentId, payload));
        clearInterval(agentPollingInterval);
        setAgentPollingInterval(null);
        handleAgentLeft();
        setIsAgentHelp(false);
        setAgentHelp(false);
        setAgentId(null);
        setAgentLeft(true);
        // Dispatch validation to stop polling on agent screen
        if (agentIdFromUrl) {
          dispatch(humanAgentValidate(agentIdFromUrl));
        }
      }, 600000); // 10 minutes

      return () => clearTimeout(timer);
    } else if (agentStatus === 'Closed' && !isAgentHelp && agentLeft) {
      clearInterval(agentPollingInterval);
      setAgentPollingInterval(null);
    } else if (agentStatus === 'Closed' && agentLeft) {
      setIsAgentHelp(false);
      setAgentHelp(false);
      handleAgentLeft();
    }
  }, [agentLeft, agentStatus, isAgentHelp]);

  // Timeout for agent responses
  useEffect(() => {
    let responseTimer;

    if (agentHelp) {
      responseTimer = setTimeout(() => {
        const payload = {
          status: 'Closed'
        };
        dispatch(humanAgentUpdateStatus(AgentId, payload));
        setAgentLeft(true);
        // Dispatch validation to stop polling on agent screen
        if (agentIdFromUrl || AgentId) {
          dispatch(humanAgentValidate(agentIdFromUrl || AgentId));
        }
      }, 600000); // 10 minutes
    }

    return () => clearTimeout(responseTimer);
  }, [answer, agentHelp]);

  // Poll for user questions on agent screen
  useEffect(() => {
    if (isAgentScreen && agentValid !== false && agentStatus !== 'Closed') {
      const interval = setInterval(async () => {
        try {
          await dispatch(getHumanAgentQnaChat(agentIdFromUrl, 'question'));
        } catch (error) {
          console.error("Error fetching user questions:", error);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [agentHelp, isAgentScreen, agentValid, agentStatus]);

  // Poll for agent status on agent screen to detect when user times out
  useEffect(() => {
    if (isAgentScreen && agentIdFromUrl && agentValid !== false && agentStatus !== 'Closed') {
      const statusInterval = setInterval(async () => {
        try {
          await dispatch(humanAgentGetStatus(agentIdFromUrl));
        } catch (error) {
          console.error("Error fetching agent status:", error);
        }
      }, 6000); // Poll every 6 seconds

      return () => clearInterval(statusInterval);
    }
  }, [isAgentScreen, agentIdFromUrl, agentValid, agentStatus]);

  // Handle status 'Closed' on agent screen (when user times out or quits)
  useEffect(() => {
    if (isAgentScreen && agentStatus === 'Closed' && agentValid !== false) {
      // If our own timer already handled expiry, skip duplicate action
      if (agentSessionTimedOut) return;

      setAgentSessionTimedOut(true);
      // Disable agent help (same as Quit Agent flow)
      setIsAgentHelp(false);
      setAgentHelp(false);
      setAgentId(null);
      setAgentLeft(true);
      setSessionTimer(null);
      setSessionStartTime(null);
      // Show toaster and redirect (same as Quit Agent)
      showToaster("Agent session duration timeout", "error");
      const qp = new URLSearchParams(location.search);
      qp.delete("agentId");
      qp.delete("cid");
      qp.delete("c");
      qp.delete("title");
      const newUrl = `${location.pathname}?${qp.toString()}`;
      trigger(newUrl);
      setMessages([]);
    }
  }, [isAgentScreen, agentStatus, agentValid]);

  // Poll for agent answers on user screen
  useEffect(() => {
    if (!isAgentScreen && agentHelp) {
      const interval = setInterval(async () => {
        try {
          await dispatch(getHumanAgentQnaChat(AgentId, 'answer'));
        } catch (error) {
          console.error("Error fetching agent answers:", error);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [agentHelp]);

  // Handle agent left cleanup
  useEffect(() => {
    if (agentLeft === true) {
      dispatch(humanAgentValidate(agentIdFromUrl || AgentId));
      dispatch(resetAnswer());
    }
  }, [agentLeft]);

  useEffect(() => {
    if (agentValid === false && agentLeft) {
      dispatch(resetAnswer());
    }
  }, [agentValid]);

  // Handle question updates from Redux
  useEffect(() => {
    if (!question) return;

    if (type === 'question' && isAgentScreen) {
      // Dedupe: skip if we've already handled this exact question payload
      // (poll cycles return the same record until a new upsert).
      if (!questionTimestamp || lastHandledQuestionTsRef.current === questionTimestamp) {
        return;
      }
      lastHandledQuestionTsRef.current = questionTimestamp;
      if (question === 'Quit Agent') {
        if (userQuitNoticeShownRef.current) return;
        userQuitNoticeShownRef.current = true;
        setQuestionText('Quit Agent');
        setMessages((prevMessages) => {
          // Remove loader if present and add quit message
          const filtered = prevMessages.filter(msg => msg.isUserLoading !== true);
          return [
            ...filtered,
            { isQuestion: false, text: `${userName} left the chat.` }
          ];
        });
        showToaster('Live Agent Help disabled.', 'error');
        dispatch(humanAgentValidate(agentIdFromUrl));
      } else if (question === 'Agent-Help') {
        // Display friendly message on left side (as bot/answer) instead of "Agent-Help" on agent screen
        // This is the first message so just add it (no loader to replace)
        setMessages((prevMessages) => [
          ...prevMessages,
          { isQuestion: false, text: "Live Agent Help requested", isAgentMessage: true },
        ]);
      } else {
        // Replace the loader placeholder with actual user question
        notifyAgentEvent("user");
        setMessages((prevMessages) => {
          const loaderIndex = prevMessages.findIndex(msg => msg.isUserLoading === true);
          if (loaderIndex !== -1) {
            const updatedMessages = [...prevMessages];
            updatedMessages[loaderIndex] = { isQuestion: false, text: question, isAgentMessage: true };
            return updatedMessages;
          }
          // No loader found - add as new message
          return [...prevMessages, { isQuestion: false, text: question, isAgentMessage: true }];
        });
      }
    }
    // On user screen, user's own questions are already added by handleUserQuestion
    // No need to add them again here - skip to avoid duplicates
  }, [question, type, questionTimestamp]);

  // Handle answer updates from Redux
  useEffect(() => {
    if (!answer) return;

    // Skip adding messages if agent session has ended (agentLeft = true)
    // This prevents duplicate messages from stale polling responses after timeout
    if (agentLeft) {
      dispatch(resetAnswer());
      return;
    }

    // Dedupe per-field timestamp so identical answer text repeats are still rendered
    // once per send (and not multiple times per poll cycle).
    if (!answerTimestamp || lastHandledAnswerTsRef.current === answerTimestamp) {
      return;
    }
    lastHandledAnswerTsRef.current = answerTimestamp;

    if (type === 'answer' && isAgentScreen) {
      // On agent screen, answer is from user (display as their question)
      // Filter out "Agent-Help" - don't show it on agent screen
      if (answer === 'Agent-Help') return;

      notifyAgentEvent("user");
      setMessages((prevMessages) => [
        ...prevMessages,
        { isQuestion: true, text: answer },
      ]);
    } else if (type === 'answer' && !isAgentScreen) {
      // On user screen, answer is from agent (display as bot response)
      if (answer === 'Quit Agent') {
        clearInterval(agentPollingInterval);
        setAgentPollingInterval(null);
        setIsAgentHelp(false);
        setAgentHelp(false);
        setAgentId(null);
        setAgentLeft(true);
        setMessages((prevMessages) => {
          // Remove the loader and add the agent left message in its place
          const filtered = prevMessages.filter(msg => msg.isAgentLoading !== true);
          return [
            ...filtered,
            { isQuestion: false, text: "Agent Left the chat", isAgentSystemMessage: true },
          ];
        });
        showToaster("Agent Left the chat", "error");
      } else {
        // Replace the loader placeholder with actual agent response
        notifyAgentEvent("agent");
        setMessages((prevMessages) => {
          const loaderIndex = prevMessages.findIndex(msg => msg.isAgentLoading === true);
          if (loaderIndex !== -1) {
            const updatedMessages = [...prevMessages];
            updatedMessages[loaderIndex] = { isQuestion: false, text: answer, isAgentMessage: true };
            return updatedMessages;
          }
          // Fallback: add as new message if no loader found
          return [...prevMessages, { isQuestion: false, text: answer, isAgentMessage: true }];
        });
      }
    }
  }, [answer, type, agentLeft, answerTimestamp]);

  // Handle AgentResponse updates
  useEffect(() => {
    if (!AgentResponse) return;

    // Skip adding messages if agent session has ended (agentLeft = true)
    // This prevents duplicate messages from stale responses after timeout
    if (agentLeft) {
      dispatch(resetAgentResponse());
      return;
    }

    // On agent screen, agent's own answers are already added by handleAgentAnswer
    // Only add system messages on agent screen, skip regular answers
    if (isAgentScreen) {
      dispatch(resetAgentResponse());
      return;
    }

    if (AgentResponse === "Connecting you to Live Chat Support now...Please wait." ||
      AgentResponse === "We have notified the Support team!\nSomeone will join you in about 2 minutes." ||
      AgentResponse === "Please Configure Agent Email to connect to Live Chat Support." ||
      AgentResponse === "Support agent joined the chat." ||
      AgentResponse === "Hey! I'm here to help. I'm just catching up on the conversation so far to understand the issue. Give me just a moment and I'll be ready to help!") {
      setMessages((prevMessages) => [
        ...prevMessages,
        { isQuestion: false, text: AgentResponse },
      ]);
      dispatch(resetAgentResponse());
    } else if (AgentResponse && AgentResponse !== 'Quit Agent') {
      // Non-system messages from agent show as agent's message
      setMessages((prevMessages) => [
        ...prevMessages,
        { isQuestion: true, text: AgentResponse },
      ]);
    }
  }, [AgentResponse, agentLeft]);

  // Handle UserQuestion updates
  useEffect(() => {
    // User questions handled elsewhere:
    // - On user screen: handleUserQuestion adds the message
    // - On agent screen: [question, type] useEffect handles via polling
    // No need to add messages here - only handle cleanup
    if (UserQuestion === 'Quit Agent') {
      dispatch(resetUserQuestion());
    }
  }, [UserQuestion, responseType]);

  // ✅ Removed: Clear messages useEffect - agent screen now shows chat history

  // Reset Redux states when agentHelp is false
  useEffect(() => {
    if (!agentHelp) {
      dispatch(resetUserQuestion());
      dispatch(resetAgentResponse());
      dispatch(resetQuestion());
      dispatch(resetAnswer());
    }
  }, [agentHelp]);

  // Format timer display (MM:SS)
  const formatTimer = (seconds) => {
    if (seconds === null) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const hasJoinedAgentSession = agentStatus === 'Active' || agentJoined;
  const hasSessionEnded =
    agentLeft ||
    agentStatus === 'Closed' ||
    agentSessionTimedOut ||
    QuestionText === 'Quit Agent' ||
    question === 'Quit Agent' ||
    answer === 'Quit Agent' ||
    AgentResponse === 'Quit Agent' ||
    UserQuestion === 'Quit Agent';

  // Live agent request status for the header badge
  let agentRequestStatus = null;
  if (!isAgentScreen && !hasSessionEnded) {
    if (hasJoinedAgentSession) agentRequestStatus = "joined";
    else if (isAgentHelp || agentHelp) agentRequestStatus = "in_progress";
  }
  if (!agentRequestStatus && !isAgentScreen && agentRequestFailed) agentRequestStatus = "failed";

  const showExternalQuitAgent =
    hasJoinedAgentSession &&
    !hasSessionEnded &&
    sessionTimer !== 0 &&
    !(isAgentScreen && agentValid === false);

  const handleExternalQuitAgent = async () => {
    if (isAgentScreen && agentValid !== false) {
      await handleAgentAnswer('Quit Agent');
    } else {
      await handleUserQuestion('Quit Agent');
    }
  };

  return (
    <div className="external-user-wrapper">
      {/* Client Logo Header */}
      <div className="client-logo-header">
        <img
          src="/images/External-user-logo.png"
          alt="Innferre Logo"
          className="client-logo"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="external-header-actions">
          <AgentRequestStatus status={agentRequestStatus} />
          {showExternalQuitAgent && (
            <button
              className="external-quit-agent-btn"
              onClick={handleExternalQuitAgent}
              aria-label="Stop Live Agent"
              data-cy="external-quit-agent-btn-cy"
            >
              ✕ Stop Live Agent
            </button>
          )}
        </div>
      </div>

      <div className="chat-container chat-view-opposite">
        {toasterVisible && (
          <CustomToaster
            message={toasterMessage}
            duration={2500}
            onClose={closeToaster}
            position="right-top"
            appearance={toasterAppearance}
          />
        )}

        <div ref={messagesDivRef} className="chat-message-area">
          {messages.map((m, idx) => (
            <div key={idx} className={`message ${m.isQuestion ? "question" : "answer"}`}>
              {m.isQuestion && (
                <div className="icons-question">
                  <img src="/images/user-chat-icon.svg" alt="user icon" />
                </div>
              )}
              <div className="outer-chat-bubble">
                {!m.isQuestion && (
                  <div className="icons-answer">
                    <img src="/images/question-chat-icon.svg" alt="bot icon" />
                  </div>
                )}
                <div className="question-box" data-cy="ai-response-cy">
                  {m.text === "Generating Response..." ? (
                    (agentHelp || isAgentScreen) ? null : <LoadingDots />
                  ) : m.isQuestion ? (
                    <>
                      {m.text}
                      {m.timestamp && (
                        <Popup
                          inverted
                          position="top center"
                          content={formatMessageTimestampFull(m.timestamp)}
                          trigger={
                            <div
                              className="message-timestamp"
                              data-cy="message-timestamp"
                            >
                              {formatMessageTimestamp(m.timestamp)}
                            </div>
                          }
                        />
                      )}
                    </>
                  ) : (() => {
                    // Inject the link whenever the feature is available so
                    // the rendered text is stable across renders. The
                    // anchor renderer drops to plain text once Live Agent
                    // Help is already active, suppressing further clicks.
                    const isFeatureAvailable = !!(DocSettingsvalue?.serviceAgentEnabled && agentEmailAvailable);
                    const isDisabled = !!(agentHelp || isAgentHelp || isAgentScreen);
                    const anchorRenderer = isFeatureAvailable
                      ? buildAgentHelpAnchorRenderer(handleAgentHelp, { isDisabled })
                      : null;
                    const componentsForMessage = anchorRenderer
                      ? { ...mdComponents, a: anchorRenderer }
                      : undefined;
                    const textForMessage = isFeatureAvailable ? injectAgentHelpLink(m.text) : m.text;
                    return (
                      <>
                      <TypewriterMarkdown
                        text={textForMessage}
                        speed={5}
                        components={componentsForMessage}
                        onComplete={
                          idx === messages.length - 1
                            ? () => {
                                if (messagesDivRef.current && waitingForResponseRef.current) {
                                  const container = messagesDivRef.current;
                                  const responseText = m.text || "";
                                  const lineCount = responseText.split("\n").length;
                                  waitingForResponseRef.current = false;
                                  container.style.paddingBottom = "";
                                  if (lineCount > 3) {
                                    // Long response — preserve the user's scroll position
                                    // so they can read from the top of the response
                                    const savedScroll = container.scrollTop;
                                    container.style.overflowY = "hidden";
                                    container.scrollTop = savedScroll;
                                    requestAnimationFrame(() => {
                                      container.scrollTop = savedScroll;
                                      container.style.overflowY = "auto";
                                    });
                                  } else {
                                    // Short response (agent comms, 1-3 lines) — scroll to bottom
                                    container.scrollTop = container.scrollHeight;
                                  }
                                }
                              }
                            : undefined
                        }
                      />
                      {/* Image display (matching ChatBot.js) */}
                      {m.imageBlobURLs && m.imageBlobURLs.length > 0 && (
                        <div>
                          {m.imageBlobURLs.map(
                            (filename, imgIndex) =>
                              filename && (
                                <AuthenticatedImage
                                  imgKey={imgIndex}
                                  relativePath={filename.trim()}
                                  apiEndpoint={m.imageApiEndpoint}
                                  style={{
                                    maxWidth: "100%",
                                    height:
                                      m.showImagesOption === "thumbnails(fullPages)"
                                        ? "150px"
                                        : "auto",
                                    padding: "10px",
                                    boxSizing: "border-box",
                                    cursor: "zoom-in",
                                    border:
                                      m.showImagesOption === "thumbnails(fullPages)" ||
                                        m.showImagesOption === "fullPages"
                                        ? "1px solid #f4f4f6"
                                        : "none",
                                    margin:
                                      m.showImagesOption === "thumbnails(fullPages)"
                                        ? "5px"
                                        : "none",
                                  }}
                                  alt={`image-${imgIndex}`}
                                  onClick={() =>
                                    openViewer(m.imageBlobURLs, m.imageBlobURLs.length > 1 ? imgIndex - 1 : imgIndex, m.imageApiEndpoint)}
                                />
                              )
                          )}
                        </div>
                      )}
                      {/* Source cards with accordion */}
                      {m.sourceData && m.sourceData.length > 0 && (
                        <>
                          <hr style={{ marginTop: "20px" }} />
                          <Accordion fluid>
                            <Accordion.Title
                              active={sourceActiveIndex === 0}
                              index={0}
                              onClick={handleSourceAccordionClick}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <h4>Sources</h4>
                              {sourceActiveIndex === 0 ? (
                                <FaChevronUp data-cy="ai-assitant-source-open" />
                              ) : (
                                <FaChevronDown data-cy="ai-assitant-source-close" />
                              )}
                            </Accordion.Title>
                            <Accordion.Content active={sourceActiveIndex === 0}>
                              {renderCards(m.sourceData)}
                            </Accordion.Content>
                          </Accordion>
                        </>
                      )}
                      {m.isAgentMessage && m.timestamp && (
                        <Popup
                          inverted
                          position="top center"
                          content={formatMessageTimestampFull(m.timestamp)}
                          trigger={
                            <div
                              className="message-timestamp"
                              data-cy="message-timestamp"
                            >
                              {formatMessageTimestamp(m.timestamp)}
                            </div>
                          }
                        />
                      )}
                      </>
                    );
                  })()}
                </div>
                {!m.isQuestion && m.text !== "Generating Response..." && !m.isInitialGreeting && (
                  <div className="bubble-float-icon">
                    <Popup
                      trigger={
                        <img
                          src="/images/copy-icon.svg"
                          alt="Copy"
                          onClick={() => copyToClipboard(m.text)}
                          data-cy="copytoclipboard-click-cy"
                        />
                      }
                      content={<span>Copy To clipboard</span>}
                      position="top center"
                      inverted
                    />
                  </div>
                )}
              </div>
              {/* Satisfaction / Feedback flow for last bot response.
                  Shown only for QnA bot responses. Excluded for human-agent
                  messages and agent system notices (join/leave). The Live
                  Agent Help button inside the card is individually disabled
                  when an agent help session is active (see below). */}
              {(!m.isQuestion && m.text !== "Generating Response..." && !m.isInitialGreeting && idx === messages.length - 1 && !m.isAgentSystemMessage && !m.isAgentMessage) && (
                <>
                  <div className="satisfaction-card" style={{ clear: "both", marginLeft: "20px" }}>
                    <p style={{ marginTop: "10px", marginBottom: "5px" }}>Are you satisfied with the answer?</p>
                    <p
                      className="satisfaction-link"
                      onClick={likeMap[m.chatConversationId] ? undefined : () => handleSatisfaction(true)}
                      style={{
                        cursor: likeMap[m.chatConversationId] ? 'not-allowed' : 'pointer',
                        opacity: likeMap[m.chatConversationId] ? 0.5 : 1,
                        pointerEvents: likeMap[m.chatConversationId] ? 'none' : 'auto'
                      }}
                    >
                      Yes
                    </p>
                    <p
                      className="satisfaction-link"
                      onClick={likeMap[m.chatConversationId] ? undefined : () => handleSatisfaction(false)}
                      style={{
                        cursor: likeMap[m.chatConversationId] ? 'not-allowed' : 'pointer',
                        opacity: likeMap[m.chatConversationId] ? 0.5 : 1,
                        pointerEvents: likeMap[m.chatConversationId] ? 'none' : 'auto'
                      }}
                    >
                      No
                    </p>
                    {!m.isRegenerate && m.text !== "Agent Left the chat" && m.text !== "The support agents are busy. A support ticket has been created, and an agent will reach out to you shortly." && (
                      <Popup
                        inverted
                        position="top center"
                        content="Generate new response to your query"
                        trigger={
                          <p className="satisfaction-link" onClick={handleRegenerate}>Regenerate</p>
                        }
                      />
                    )}
                    {DocSettingsvalue?.serviceAgentEnabled && agentEmailAvailable && (() => {
                      const isAgentHelpDisabled = !!(agentHelp || isAgentHelp || isAgentScreen);
                      return (
                        <Popup
                          inverted
                          position="top center"
                          content="Connect with a live support agent"
                          trigger={
                            <p
                              className="satisfaction-link"
                              onClick={isAgentHelpDisabled ? undefined : handleAgentHelp}
                              style={{ cursor: isAgentHelpDisabled ? 'not-allowed' : 'pointer', opacity: isAgentHelpDisabled ? 0.5 : 1, pointerEvents: isAgentHelpDisabled ? 'none' : 'auto' }}
                            >Live Agent Help</p>
                          }
                        />
                      );
                    })()}
                  </div>

                  {/* Inline feedback options shown when clicking No */}
                  {showFeedbackOptions && (
                    <div className="feedback-options-card">
                      <p>Please provide more details:</p>
                      <div>
                        {(suggestionData || []).map((option, i) => (
                          <p key={i} className={`feedback-link${selectedFeedbackOption === option.title ? " feedback-link-selected" : ""}`} onClick={() => handleFeedbackOptionClick(option.title)}>{option.title}</p>
                        ))}
                        {DocSettingsvalue?.serviceAgentEnabled && agentEmailAvailable && (() => {
                          const isAgentHelpDisabled = !!(agentHelp || isAgentHelp || isAgentScreen);
                          return (
                            <Popup
                              inverted
                              position="top center"
                              content="Connect with a live support agent"
                              trigger={
                                <p
                                  className="feedback-link"
                                  onClick={isAgentHelpDisabled ? undefined : handleAgentHelp}
                                  style={{ cursor: isAgentHelpDisabled ? 'not-allowed' : 'pointer', opacity: isAgentHelpDisabled ? 0.5 : 1, pointerEvents: isAgentHelpDisabled ? 'none' : 'auto' }}
                                >Live Agent Help</p>
                              }
                            />
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {showOtherFeedbackInput && (
                    <div className="other-feedback-input" style={{ clear: "both", marginLeft: "20px", marginTop: "10px" }}>
                      <textarea
                        rows="3"
                        value={feedbackInput}
                        onChange={(e) => setFeedbackInput(e.target.value)}
                        placeholder="Please provide your feedback here..."
                        className="custom-ai-input"
                        data-cy="feedback-input-cy"
                      />
                      <div className="middleCenter" data-cy="input-with-icon">
                        <Image
                          className="icon-image"
                          src="/images/grey-send.svg"
                          alt="Submit Feedback"
                          onClick={submitOtherFeedback}
                          data-cy="feedback-submit-img-click-cy"
                        />
                      </div>
                    </div>
                  )}

                  {showThankYouMessage && m.text !== "Agent Left the chat" && (
                    <div className="thank-you-message" style={{ clear: "both", marginLeft: "20px", marginTop: "10px" }}>
                      <p>Thank you for your response!</p>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          {/* Inline loader above replaces overlay */}
          <div ref={messagesEndRef} />
        </div>

        <div
          className="asAssistantSearchBox"
          style={{ bottom: "44px", position: "fixed", left: 0, right: 0, zIndex: 1000, flexDirection: 'column', alignItems: 'center' }}
        >
          {/* Session Timer Display - inside fixed container so it's visible above input */}
          {sessionTimer !== null && sessionTimer !== 0 && (
            <div style={{
              width: '100%',
              maxWidth: '1200px',
            }}>
              <div style={{
                backgroundColor: sessionTimer <= 60 ? '#FF3859' : sessionTimer <= 90 ? '#FFC107' : '#F4F4F6',
                color: sessionTimer <= 60 ? 'white' : sessionTimer <= 90 ? 'white' : '#14181F',
                padding: '8px 16px',
                borderRadius: '16px 16px 0 0',
                fontWeight: '600',
                fontSize: '13px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                border: sessionTimer <= 60 ? 'none' : sessionTimer <= 90 ? 'none' : '2px solid #E3E3E8',
                borderBottom: 'none',
                animation: 'slideUpFromBehind 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transition: 'all 0.3s ease'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={sessionTimer <= 90 ? 'white' : '#0053ED'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'pulse 2s ease-in-out infinite', flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span style={{ fontWeight: '600' }}>Session Expires in {formatTimer(sessionTimer)}</span>
              </div>
            </div>
          )}
          {sessionTimer !== 0 && (
            <div className="input-with-icon asAssistantSearch">
              <textarea
                ref={textareaRef}
                rows="1"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  // Auto-resize: shrink then grow to fit content
                  e.target.style.height = 'auto';
                  const newHeight = Math.min(e.target.scrollHeight, 150);
                  e.target.style.height = newHeight + 'px';
                  // Show scrollbar only when content exceeds max height
                  e.target.style.overflowY = e.target.scrollHeight > 150 ? 'auto' : 'hidden';
                }}
                placeholder={agentIdFromUrl ? "Answer Question or Click 'Stop Live Agent' to End Session" : "Ask question"}
                onKeyPress={handleKeyPress}
                className="custom-ai-input"
                data-cy="question-ask-input-cy"
                style={{
                  borderRadius: sessionTimer !== null ? '0 16px 16px 16px' : '16px',
                  border: '2px solid #E3E3E8',
                  resize: 'none',
                  overflowY: 'hidden',
                  minHeight: '48px',
                  maxHeight: '150px',
                }}
              />
              <div className="middleCenter" data-cy="input-with-icon">
                <Image
                  className="icon-image"
                  src="/images/grey-send.svg"
                  alt="Send"
                  onClick={sendQuestion}
                  data-cy="response-img-click-cy"
                />
              </div>
            </div>
          )}
        </div>
        {/* Star rating and disclaimer below input box */}
        <div className="star-rating-wrapper">
          <StarRating />
          <p style={{ color: '#888', fontSize: '11px', textAlign: 'center', margin: '4px 0 0 0', lineHeight: '1.4' }}>
            Innferre is AI and can make mistakes. Please double-check responses.
          </p>
        </div>
      </div>
      {/* Image Lightbox Viewer */}
      {isViewerOpen &&
        currentFilenames.length > 0 &&
        currentFilenames[currentImageIndex] && (
          <Lightbox
            // lightboxSrcs is resolved by a dedicated effect keyed on
            // isViewerOpen/currentImageIndex/currentFilenames/viewerApiEndpoint
            // — falls back to a blank placeholder only until that fetch resolves.
            mainSrc={lightboxSrcs.main || BLANK_IMAGE_PLACEHOLDER}
            nextSrc={lightboxSrcs.next || BLANK_IMAGE_PLACEHOLDER}
            prevSrc={lightboxSrcs.prev || BLANK_IMAGE_PLACEHOLDER}
            onCloseRequest={() => closeViewer()}
            onMovePrevRequest={() =>
              setCurrentImageIndex(
                (currentImageIndex + currentFilenames.length - 1) %
                currentFilenames.length
              )
            }
            onMoveNextRequest={() =>
              setCurrentImageIndex(
                (currentImageIndex + 1) % currentFilenames.length
              )
            }
            imagePadding={50}
          />
        )}
      {/* Feedback Modal (match normal flow) */}
      <Modal
        open={thubmsUpModel}
        onClose={handleClose}
        size="tiny"
        className="add-document-model filterPopup feedbackPopup"
      >
        <Modal.Content>
          <Header as="h3">Please Share your feedback and help us get better</Header>
          <Grid>
            {suggestionOptions && suggestionOptions.length > 0 ? (
              <Grid.Row>
                {suggestionOptions.map((val, index) => (
                  <Grid.Column width={8} key={index} className="feedbackFullWidth">
                    <Form.Field
                      onClick={() => handleTableRowClick(val._id, val.title)}
                      data-cy="suggestion-radio-select-cy"
                    >
                      <Radio
                        className="datasetRadioOption"
                        label={val.title}
                        name="radioGroup"
                        value={val._id}
                        checked={radioValues ? radioValues.value === val._id : false}
                        onChange={handleSelectRadio}
                      />
                    </Form.Field>
                  </Grid.Column>
                ))}
              </Grid.Row>
            ) : (
              <>
                <Grid.Row className="skelton-chat-radio-row">
                  <Grid.Column width={8} className="skelton-chat-radio-column">
                    <Form.Field>
                      <div className="skeleton-view skelton-chat-radio-column-div"></div>
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column width={8} className="skelton-chat-radio-column">
                    <Form.Field>
                      <div className="skeleton-view skelton-chat-radio-column-div"></div>
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              </>
            )}
          </Grid>
          <br />
          <br />
          <label>Desired response</label>
          <br />
          <Input
            type="text"
            value={inputValue}
            onChange={handleChange}
            placeholder="Feel free to add any specific details"
            className="add-datset-input"
            data-cy="feedback-comments-input-cy"
          />
          <br />
        </Modal.Content>
        <Modal.Actions>
          <button className="cancel-btn" onClick={handleClose} data-cy="feedback-cancel-submit-btn">
            Cancel
          </button>
          <button
            disabled={!isButtonEnabled}
            className="add-btn"
            onClick={handleThubmsUpPopup}
            data-cy="feedback-submit-btn"
          >

            Submit
          </button>
          {DocSettingsvalue?.serviceAgentEnabled && agentEmailAvailable && (
            <button
              className="add-btn"
              onClick={() => { handleClose(); handleAgentHelp(); }}
              data-cy="feedback-live-agent-btn"
              style={{ marginLeft: '10px' }}
            >
              Live Agent Help
            </button>
          )}
        </Modal.Actions>
      </Modal>

      {/* Live Agent Help Modal - Collect Email and Company Name */}
      <Modal
        open={showAgentHelpModal}
        onClose={handleAgentHelpModalClose}
        size="tiny"
        className="add-document-model filterPopup feedbackPopup"
      >
        <Modal.Content>
          <Header as="h3">Connect to Live Agent Support</Header>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Please provide your contact information to connect with a live agent.
          </p>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Email Address <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              type="email"
              value={agentHelpUserEmail}
              onChange={(e) => {
                const val = e.target.value;
                setAgentHelpUserEmail(val);
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!val || !val.trim()) {
                  setAgentHelpModalError("Please enter your email address.");
                } else if (!emailRegex.test(val.trim())) {
                  setAgentHelpModalError("Please enter a valid email address.");
                } else {
                  setAgentHelpModalError("");
                }
              }}
              onBlur={() => {
                if (!agentHelpUserEmail || !agentHelpUserEmail.trim()) {
                  setAgentHelpModalError("Please enter your email address.");
                } else {
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(agentHelpUserEmail.trim())) {
                    setAgentHelpModalError("Please enter a valid email address.");
                  }
                }
              }}
              placeholder="Enter your email address"
              className="add-datset-input"
              data-cy="agent-help-email-input"
              fluid
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Company Name <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              type="text"
              value={agentHelpCompanyName}
              onChange={(e) => {
                setAgentHelpCompanyName(e.target.value);
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (agentHelpModalError && agentHelpModalError.toLowerCase().includes('company')) {
                  setAgentHelpModalError("");
                } else if (agentHelpModalError && emailRegex.test(agentHelpUserEmail.trim())) {
                  setAgentHelpModalError("");
                }
              }}
              placeholder="Enter your company name"
              className="add-datset-input"
              data-cy="agent-help-company-input"
              fluid
            />
          </div>
          {agentHelpModalError && (
            <div style={{
              marginTop: '15px',
              padding: '12px 16px',
              backgroundColor: '#FFF5F5',
              border: '1px solid #FFE8E8',
              borderLeft: '4px solid #FF3859',
              borderRadius: '8px',
              color: '#14181F',
              fontSize: '14px',
              lineHeight: '1.6',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF3859" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span style={{ flex: 1 }}>{agentHelpModalError}</span>
            </div>
          )}
          <div style={{ marginTop: '15px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <input
              type="checkbox"
              id="agent-help-consent-checkbox"
              checked={agentHelpConsentChecked}
              onChange={(e) => setAgentHelpConsentChecked(e.target.checked)}
              data-cy="agent-help-consent-checkbox"
              style={{ marginTop: '3px', cursor: 'pointer', flexShrink: 0 }}
            />
            <label
              htmlFor="agent-help-consent-checkbox"
              style={{ fontSize: '13px', color: '#555', lineHeight: '1.5', cursor: 'pointer' }}
            >
              <em>By checking this box, you are agreeing that your user contact information will be shared with third-party service providers, including AI-powered chatbot platforms and customer support tools, to facilitate assistance.</em>
            </label>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <button
            className="cancel-btn"
            onClick={handleAgentHelpModalClose}
            data-cy="agent-help-cancel-btn"
          >
            Cancel
          </button>
          <button
            disabled={!agentHelpUserEmail.trim() || !agentHelpCompanyName.trim() || !!agentHelpModalError || !agentHelpConsentChecked}
            className="add-btn"
            onClick={handleAgentHelpSubmit}
            data-cy="agent-help-submit-btn"
            style={{ width: '35%' }}
          >
            Connect to Agent
          </button>
        </Modal.Actions>
      </Modal>

      {/* PDF Preview Modal */}
      {isPreviewModalOpen && previewDocUrl && (
        <PDFViewer
          url={previewDocUrl}
          isOpen={isPreviewModalOpen}
          onClose={handleClosePreviewModal}
          filename={previewDocFilename}
          pageNumber={previewPageNumber}
        />
      )}
    </div>
  );
}

export default ExternalUser;
